import {useCallback, useEffect, useMemo, useRef} from "react";
import {v4 as uuid4} from "uuid";
import {observable, runInAction, toJS, makeAutoObservable, isObservableArray} from "mobx";
import {toast} from "react-toastify";
import {useStreamConnector} from "../hooks/useStreamConnector";
import {captureMessage, isImage, safeParseJSON, saveBlob, parseContentDisposition, wait} from "../lib/utils";
import {DEV, getConfig, getVersionInfo} from "../lib/configMgr";
import {cancelAgent, fetchAgentStatus} from "../queries/search";
import {fetchChatCompletion, fetchHelp, oneshot} from "../queries/autosuggest";
import {LLM_REQUEST_TIMEOUT} from "../queries/request";
import {ROLE_SYSTEM, ROLE_USER, ROLE_ASSISTANT} from "./roles";
import useWatchdog from "../hooks/useWatchdog";
import {TEXT_PLACEHOLDER} from "../templates/slides";
import * as Analytics from "../lib/Analytics";

const { ExternalRESTAPI } = getConfig();

// Avoid inadvertent timeouts while debugging
const STREAM_TIMEOUT = DEV ? 6000000 : 180000;
// Timeout for the initial POST before the stream_id is returned. Referenced
// lazily (POST_TIMEOUT below) rather than aliased at module-eval time: request.js
// sits in a large import cycle (lib/utils -> lib/links -> markdown/interpolation
// -> ... -> lib/SearchContext -> queries/request -> lib/utils), so Rollup's
// module ordering can run this file's top-level code before request.js's own
// LLM_REQUEST_TIMEOUT declaration executes, throwing a TDZ ReferenceError.
// Must exceed MAX_GATEWAY_RETRIES * 30s backoff in request.js (currently 2 * 30s = 60s plus request time).
const POST_TIMEOUT = () => LLM_REQUEST_TIMEOUT;

const SSE_EVENT_TYPES = [
  "search_update",
  "search_results",
  "message_start",
  "content_block_start",
  "content_block_delta",
  "content_block_stop",
  "interrupt",
  "message_delta",
  "message_stop",
  "message",
  "ping",
  "heartbeat",
  "metadata",
  "toast",
];

// Tools whose results must be preserved in full — they carry ID→label mappings
// that the guide relies on to correctly attribute search results.
const PRESERVE_TOOL_RESULT_NAMES = new Set(['resolve', 'fetch_entities', 'validate_plex_ids']);

// Optimize sub-agent cached messages for persistence:
// - toolResult blocks must always be present (Bedrock schema requires pairing with toolUse)
// - For large-output tools: keep the shell but strip content to save space
// - For resolve/fetch_entities: preserve content in full (ID→label mappings are critical)
// Exported so any conversationWithContext()-style save-payload builder (e.g.
// ChatSession.js's own, separate from this class's method) can serialize
// `client.subAgentTurns` the same way — see PLEX-6300 follow-up.
export const optimizeAgentMessages = (messages) => {
  if (!messages || messages.length === 0) return null;

  // Build toolUseId → tool name map from this message array
  const toolNames = {};
  messages.forEach(msg => {
    (msg.content || []).forEach(block => {
      if (block.toolUse) toolNames[block.toolUse.toolUseId] = block.toolUse.name;
    });
  });

  const optimized = messages.map(msg => {
    if (!msg.content) return msg;
    const optimizedContent = msg.content.map(block => {
      if (!block.toolResult) return block;
      const toolName = toolNames[block.toolResult.toolUseId];
      if (PRESERVE_TOOL_RESULT_NAMES.has(toolName)) return block;
      // Keep shell (required for schema pairing), strip content
      return {...block, toolResult: {toolUseId: block.toolResult.toolUseId, content: []}};
    });
    return {...msg, content: optimizedContent};
  });
  return optimized.length > 0 ? optimized : null;
};

export const cleanTurn = (turn, forPersistence = false) => {
  if (!turn) return turn;
  // subAgentTurns lives at the top level of the client/conversation now, never
  // nested per-turn — nothing to strip/optimize here. See
  // LLMClient.conversationWithContext for where the top-level dict is optimized.
  if (turn.toolUseInfo){
    delete turn.toolUseInfo;
  }
  // Strip streaming-only flags that must never be persisted or sent to the API.
  // `partial` on a turn or block means the stream was still open when this was saved;
  // leaving it in storage causes the conversation to reload with a permanently-stuck
  // partial-message UI state.
  if (turn.partial) delete turn.partial;
  if (turn.content) {
    turn.content.forEach(block => {
      if (block.partial) delete block.partial;
      if (block.suppressFinalAnswer !== undefined) delete block.suppressFinalAnswer;
    });
  }
  return turn;
};

export const useLLMClient = (sessionAgent, searchParams, auth, userStore = null, appStatusStore = null) => {
  // Create client once per sessionAgent - don't recreate on searchParams changes
  // searchParams is only used when sending prompts, not during construction
  const client = useMemo(() =>
                           new LLMClient({
                                           sessionAgent,
                                           auth,
                                           userStore,
                                           appStatusStore,
                           }),
                         [sessionAgent]);
  const {streamId} = client;
  const [resetWatchdog, stopWatchdog] = useWatchdog(client._handleTimeout, STREAM_TIMEOUT);
  useEffect(() => {
    if (streamId === null) {
      stopWatchdog();
    }
  }, [streamId, stopWatchdog]);
  const handleStreamingResponse = useCallback((e) => {
    // This callback closes over `streamId` as of the render that created it, and
    // useStreamConnector's effect is keyed on the same value — so this instance is
    // only ever handed to the connection opened for THIS streamId. If a chunk from
    // that connection was already resolved/parsed before abort() took effect on a
    // streamId transition (see useStreamConnector's dispatchEventsInBatches), it can
    // still reach here after `client.streamId` has moved on. Drop it rather than
    // apply a stale connection's event to the now-current turn (WEBAPP-7F/7G).
    if (streamId !== client.streamId) {
      console.debug(`[LLMClient] Dropping event from abandoned stream ${streamId?.slice(0, 4)} (current: ${client.streamId?.slice(0, 4)})`);
      return;
    }
    // Reset the watchdog on every SSE event — pings prove the connection and server
    // are alive. During long parallel tool execution the backend sends only pings;
    // filtering them out starved the watchdog and caused 180s stalls (PLEX-5825).
    // Guard: only reset while a stream is active. Sub-agent SSE connections can outlive
    // the main stream close; stale events (streamId=null AND active=false) must not restart
    // a stopped watchdog and cause a spurious 180s timeout (WEBAPP-3S).
    // Also reset when active=true but streamId=null — this window exists between old-stream
    // close and new stream_id receipt during tool-result round-trips; sub-agent events in
    // that window are valid and must keep the watchdog alive.
    if (client.streamId || client.active) {
      resetWatchdog();
    }
    client.handleStreamEvent(e);
  }, [streamId]);
  const handleStreamingError = useCallback((e) => {
    // Same abandoned-connection guard as handleStreamingResponse — an error from a
    // stream this render no longer considers current shouldn't affect client state.
    if (streamId !== client.streamId) {
      return;
    }
    // On SSE disconnect while a stream is active, poll the agent status endpoint
    // so we can surface a failure immediately rather than waiting out the full
    // watchdog timeout.
    if (client.streamId && client.active) {
      fetchAgentStatus(client.agentId, client._auth).then(status => {
        // Re-check abandonment: the user may have cancelled/interrupted while this
        // fetch was in flight, nulling client.streamId. Without this, a stale error
        // for an already-cancelled stream still gets processed and reported as a
        // spurious "Connection failed" Sentry error (WEBAPP-2).
        if (streamId !== client.streamId) {
          return;
        }
        if (status && status.alive === false) {
          stopWatchdog();
          client._handleError(
            "Connection lost and agent is no longer running.\n\n" +
            "The request may have failed on the server. Please try again."
          );
        } else {
          client.handleStreamEvent(e);
        }
      }).catch(() => {
        if (streamId !== client.streamId) {
          return;
        }
        stopWatchdog();
        client.handleStreamEvent(e);
      });
    } else {
      stopWatchdog();
      client.handleStreamEvent(e);
    }
  }, [streamId]);
  const { pollingMode, getLoopHealth } = useStreamConnector({
    streamId,
    auth: client._auth,
    onEvent: handleStreamingResponse,
    onError: handleStreamingError,
    // PLEX-6528: detecting the timeout isn't enough — without this the only
    // recovery was the 180s conversation watchdog, by which point the backend
    // had usually already finished and failed to notify (stream_end published
    // to 0 subscribers), so the stream just sat dead until a hard cancel.
    // Route into the same reconnect/detach path handleStreamingError uses.
    onInterChunkTimeout: useCallback((lastEventId) => {
      client._captureStreamAnomaly("SSE inter-chunk timeout: proxy silently dropped mid-stream", {
        level: "warning",
        fingerprint: ["sse-inter-chunk-timeout"],
        tags: { lastEventId: lastEventId ?? null },
      });
      handleStreamingError(Object.assign(new Event("error"), { reason: "inter_chunk_timeout", lastEventId }));
    }, [handleStreamingError]),
    // Poll 404 is ambiguous (channel gone: clean completion vs. disconnect grace
    // window lapsed while the turn is still running — WEBAPP-3S). Check the agent
    // status endpoint before trusting it as "done", same as handleStreamingError does.
    checkAgentAlive: useCallback(async () => {
      const status = await fetchAgentStatus(client.agentId, client._auth);
      return !!status?.alive;
    }, []),
  });
  // Expose on client so ChatSession can include it in analytics events
  client.pollingMode = pollingMode;
  // PLEX-6310: expose the read-loop health snapshot for _handleTimeout to attach to
  // its Sentry capture. See recordTick's comment in useStreamConnector.js — this is
  // deliberately independent of console/breadcrumb logging.
  useEffect(() => { client._getStreamLoopHealth = getLoopHealth; }, [getLoopHealth]);
  useEffect(() => {
    const handleBeforeUnload = event => {
      client.dispose();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [client])
  useEffect(() => {
    return () => {
      client.dispose();
    }
  }, []);
  useEffect(() => {
    if (streamId) {
      resetWatchdog();
    }
    else {
      stopWatchdog();
    }
  }, [streamId]);

  return client;
};

export const TOOL_MEMBERS = "members";
export const TOOL_RESOLVE = "resolve";
export const TOOL_SEARCH = "search";
export const TOOL_RUN_AGENT = "run_agent";
// The search-analyst's own low-level search-execution call (ts_run_search() in
// rd/search plex/search/llm/tools.py) — carries the real, already-expand_members-resolved
// ids/params that were actually searched, unlike the outer TOOL_SEARCH call's input.
export const TOOL_RUN_SEARCH = "run_search";
// Analyst-specific tools — shown as progress labels to non-power users
export const ANALYST_TOOLS = new Set(["fetch_totals", "fetch_results", "fetch_entities", "fetch_neighbors"]);
// Suffixes recognized by bedrock; assume text if not in one of these
// Claude also accepts odt, rtf, epub, and json; pass these as "txt"
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_Message.html
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentBlock.html
// See https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ImageBlock.html
export const FILE_INPUT_ACCEPT = [
  ".pdf", "application/pdf",
  ".csv", "text/csv",
  ".doc", "application/msword",
  ".docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls", "application/vnd.ms-excel",
  ".xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".html", "text/html",
  ".txt", "text/plain",
  ".md", "text/markdown",
  ".plex", "application/json",
  ".png", "image/png",
  ".jpeg", "image/jpeg",
  ".gif", "image/gif",
  ".webp", "image/webp"
];
export const MIME2FORMAT = {
  "application/pdf": "pdf",
  "text/csv": "csv",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/html": "html",
  "text/plain": "txt",
  "text/tab-separated-values": "csv",
  "text/markdown": "md",
  "application/json": "txt",
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpeg",
  "image/gif": "gif",
  "image/webp": "webp"
};
export const DOC_LIMIT = 5;
export const DOC_SIZE_LIMIT = 4.5 * 1024 * 1024;
export const IMAGE_LIMIT = 20;
export const IMAGE_SIZE_LIMIT = 3.75 * 1024 * 1024;
// Canonical LLM conversation roles — defined in ./roles to avoid a circular
// import with autosuggest.js (autosuggest -> LLMClient -> autosuggest hit a TDZ
// error on LLM_REQUEST_TIMEOUT in the production bundle, WEBAPP-XXX)
export {ROLE_SYSTEM, ROLE_USER, ROLE_ASSISTANT};
// Special role for preserving conversation w/customization context
export const ROLE_CONTEXT = "context"
// Available "personalities"
export const AGENT_HELP = "help";
export const AGENT_GUIDE = "guide";
export const AGENT_ANALYST = "search-analyst";

export class LLMClient {
  _turns = [];
  _compaction = null;
  _context = null;
  _agentId = uuid4();
  _streamId = null;
  _clientEventLog = [];

  // Is there a conversation active?
  _active = false;
  // Has a prompt been sent but server not responded?
  _waitingForResponse = false;
  // Monotonic counter bumped at the start of every new send cycle (_sendPrompt,
  // _continueFromToolResults) and again by handleInterrupt. Lets a late-resolving
  // fetch (already in flight when the user interrupted) recognize it's stale and
  // avoid reviving streamId/busy state for a cycle that's already been
  // interrupted (WEBAPP interrupt-loop bug).
  _generation = 0;
  // True once handleInterrupt has fully processed an interrupt for the current
  // send cycle. The server independently raises UserInterrupt in the root
  // agent's loop AND in every still-running sub-agent's loop for a single user
  // cancel action, each publishing its own "interrupt" SSE event on the same
  // stream_id channel. Without this guard, every one of those echoes re-appends
  // its own "I was interrupted" turn, producing the repeating-bubble bug. Reset
  // to false whenever a new send cycle starts, so a genuinely new interrupt on
  // that cycle is processed normally.
  _interruptHandled = false;
  // Single flat map for the whole conversation's lifetime, keyed by toolUseId.
  // Not nested per-turn — a toolUseId is unique across the conversation, so there's
  // no reason to shard entries by which root turn originated them.
  _subAgentTurns = new Map();
  _reportedMissingAgents = new Set();
  // Fpr use with help
  _sessionId = null;
  _model = null;

  _inputTokens = 0;
  _outputTokens = 0;
  _totalTokens = 0;
  _streamStartTime = null;
  _cacheReadInputTokens = 0;
  _cacheWriteInputTokens = 0;
  _latencyMs = 0;

  _sessionAgent = null;
  _auth = null;
  _userStore = null;
  _appStatusStore = null;
  _onAssistantTurnComplete = null;
  constructor({
                sessionAgent = AGENT_HELP,
                auth = null,
                userStore = null,
                appStatusStore = null,
  }) {
    makeAutoObservable(this, {
      _sessionAgent: false,
      _onAssistantTurnComplete: false,
      // Shallow: entries are swapped wholesale (live observable.array while a sub-agent
      // streams, plain array once it reaches end_turn) rather than deep-observed in place.
      // Deep (the makeAutoObservable default) would keep every string/object node in a
      // finished sub-agent's turns wrapped in MobX Proxies/Atoms forever, even though
      // nothing observes them again after that sub-agent's toolResult closes.
      _subAgentTurns: observable.shallow,
    });
    this._sessionAgent = sessionAgent;
    this._auth = auth;
    this._userStore = userStore;
    this._appStatusStore = appStatusStore;
  }

  /**
   * Register a callback fired whenever a complete turn is added or finalized
   * in the conversation — user prompts, assistant responses, and tool-result
   * turns (sub-agent updates).  Used to persist incremental progress.
   */
  set onTurnComplete(fn) { this._onAssistantTurnComplete = fn; }
  get active() {return this._active;}
  set active(isActive) {this._active = isActive;}
  get turns() {
    return this._turns;
  }
  get compaction() {return this._compaction;}
  set compaction(v) {this._compaction = v;}
  get agentId() {return this._agentId;}
  get streamId() {return this._streamId;}
  set streamId(streamId) {
    const cancelAgents = this.active
                         && this._streamId !== null && streamId === null;
    this._streamId = streamId;
    if (streamId !== null) {
      this._clientEventLog = [];
      this._staleEventReported = false;
      this._streamStartTime = Date.now();
    }
    if (streamId === null) {
      this.active = false;
      this._reportedMissingAgents.clear();
      // Do not clear subAgentTurns here: it holds every sub-agent's turns for the
      // whole conversation, not just the stream that just ended. A stream ending
      // normally (end_turn) doesn't mean those turns are stale — the next
      // user-submitted turn reuses the same client instance and conversation.
      // Anything still present with a live (still-mobx-observable) array here
      // either genuinely finished without a matching cleanup (a bug worth its
      // own fix, not papered over by a blanket clear) or is mid-cancellation, in
      // which case _cancelAgents (below) needs it intact to know which agent IDs
      // to send cancel requests for — clearing it first made that always see an
      // empty map.
    }
    if (cancelAgents) {
      this._cancelAgents(true);
    }
  }
  get waitingForResponse() {return this._waitingForResponse;}
  set waitingForResponse(waiting) {this._waitingForResponse = waiting;}
  get subAgentTurns() {return this._subAgentTurns;}
  get sessionAgent() {return this._sessionAgent;}
  get auth() {return this._auth;}
  get context() {return this._context;}
  get model() {return this._model;}
  set model(modelId) {this._model = modelId;}
  get latencyMs() {return this._latencyMs;}
  set latencyMs(ms) {this._latencyMs = ms;}
  get inputTokens() {return this._inputTokens;}
  set inputTokens(tokens) {this._inputTokens = tokens;}
  get outputTokens() {return this._outputTokens;}
  set outputTokens(tokens) {this._outputTokens = tokens;}
  get totalTokens() {return this._totalTokens;}
  set totalTokens(tokens) {this._totalTokens = tokens;}
  get cacheReadInputTokens() {return this._cacheReadInputTokens;}
  set cacheReadInputTokens(tokens) {this._cacheReadInputTokens = tokens;}
  get cacheWriteInputTokens() {return this._cacheWriteInputTokens;}
  set cacheWriteInputTokens(tokens) {this._cacheWriteInputTokens = tokens;}

  /**
   * Serialize the current conversation state to a JSON string.
   * Returns the JSON string and the parsed object.
   */
  _serializeConversation = () => {
    const obj = {
      streamId: this.streamId,
      active: this.active,
      sessionAgent: this.sessionAgent,
      agentId: this.agentId,
      turns: toJS(this._turns).map(turn => turn ? ({
        ...turn,
        content: (turn.content || []).filter(b => !(b.toolResult && !b.toolResult.content?.length)),
      }) : turn),
      compaction: toJS(this._compaction),
      subAgentTurns: Object.fromEntries(
        Array.from(this.subAgentTurns.entries()).map(([k, v]) => [k, toJS(v)])
      ),
    };
    return { obj, json: JSON.stringify(obj, null, 2) };
  };

  /**
   * Build context/attachment options for captureMessage based on conversation size.
   *
   * If the serialized conversation fits within INLINE_THRESHOLD bytes, it goes into
   * Sentry context (visible inline in the UI). Otherwise a summary goes into context
   * and the full JSON goes as an attachment.
   *
   * @param {Object} errorContext - Extra fields to merge into the Sentry context object.
   * @returns {{ context: Object, attachments?: Array, data?: undefined }}
   */
  _buildConversationCapture = (errorContext = {}) => {
    const INLINE_THRESHOLD = 8192;
    const { obj, json } = this._serializeConversation();

    const turns = obj.turns || [];
    const summarizeTurn = t => {
      const block = t.content?.[0];
      let preview = null;
      if (block?.text) preview = block.text.slice(0, 150);
      else if (block?.toolUse) preview = `toolUse: ${block.toolUse.name}`;
      else if (block?.toolResult) preview = `toolResult: ${String(block.toolResult.content).slice(0, 150)}`;
      return { role: t.role, preview };
    };

    const summary = {
      turnCount: turns.length,
      agentId: obj.agentId,
      sessionAgent: obj.sessionAgent,
      streamId: obj.streamId,
      lastTurns: turns.slice(-2).map(summarizeTurn),
      ...errorContext,
    };

    if (json.length <= INLINE_THRESHOLD) {
      // Small enough to include fully inline — still use summary format for consistency,
      // but attach the full object too so nothing is lost.
      return {
        context: { conversation: { ...summary, full: obj } },
      };
    }

    // Large conversation — summary in context, full JSON as attachment.
    return {
      context: { conversation: summary },
      attachments: [{
        filename: "conversation.json",
        data: json,
        contentType: "application/json",
      }],
    };
  };

  _handleTimeout = () => {
    // Guard: if the stream is already finished, this is a stale timer (e.g. tab-visibility
    // race where visibilitychange reschedules before the React stopWatchdog effect fires).
    if (!this.active && !this.streamId) return;

    // PLEX-6111: distinguish a backgrounded/throttled tab (pings kept arriving server-side,
    // resetWatchdog just never ran because the tab was hidden) from a genuinely dead
    // connection, without having to reconstruct it after the fact from separate breadcrumbs.
    const visibilityState = typeof document !== "undefined" ? document.visibilityState : "unknown";
    // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): this embeds
    // streamId per call, so Sentry's captureConsoleIntegration can't group
    // occurrences and spawns a fresh noise issue per firing (WEBAPP-9Z/AB/AC).
    console.debug(
      `[LLMClient] Watchdog firing: streamId=${this.streamId} active=${this.active} ` +
      `openSubAgents=${Array.from(this.subAgentTurns.keys()).filter(id => isObservableArray(this.subAgentTurns.get(id))).length} ` +
      `documentVisibility=${visibilityState}`
    );

    // Check what the agent was doing when it timed out to provide context
    const lastTurn = this.turns.length > 0 ? this.turns[this.turns.length - 1] : null;
    let context = "";
    let operation = "unknown operation";

    if (lastTurn?.role === ROLE_USER) {
      // Check for tool use to identify the operation
      const toolUse = lastTurn.content?.find(b => b.toolResult);
      if (toolUse?.toolResult) {
        const toolUseId = toolUse.toolResult.toolUseId;
        // Try to find the original tool use from a previous turn
        for (let i = this.turns.length - 2; i >= 0; i--) {
          const turn = this.turns[i];
          const foundToolUse = turn.content?.find(b => b.toolUse?.toolUseId === toolUseId);
          if (foundToolUse) {
            operation = foundToolUse.toolUse.name || "tool execution";
            break;
          }
        }
      }
    } else if (lastTurn?.role === ROLE_ASSISTANT) {
      // Check if assistant was invoking a tool
      const toolUse = lastTurn.content?.find(b => b.toolUse);
      if (toolUse) {
        operation = toolUse.toolUse.name || "tool execution";
      }
    } else if (lastTurn?.role === ROLE_SYSTEM) {
      const systemMsg = lastTurn.content?.[0]?.text;
      if (systemMsg) {
        operation = systemMsg.toLowerCase().includes("search") ? "search" : "processing";
      }
    }

    context = ` during ${operation}`;

    this._handleError(
      `Watchdog timeout: No streaming events received for 180 seconds${context}.\n\n` +
      `This usually indicates:\n\n` +
      `- The server is processing a very complex query\n` +
      `- There may be an issue with the data being requested\n` +
      `- Network connectivity problems\n\n` +
      `Try simplifying your query or trying again.`,
      { includeEventsSnapshot: true, extraTags: { documentVisibility: visibilityState } }
    );
  }

  _handleError = (error, { includeEventsSnapshot = false, extraTags = {} } = {}) => {
    const timestamp = new Date().toISOString();
    const errorMessage = typeof error === 'string' ? error : error?.message || String(error);

    // Categorize error type for better fingerprinting and grouping
    let errorType = "generic";
    let fingerprint = ["llm-error"];
    let entityId = null;
    let entityType = null;

    // Check for entity-related errors (SMILES, ChEMBL, etc.)
    // Patterns: "Error: 'smiles:...'" or "KeyError: 'chembl:...'" or bare entity IDs
    const entityErrorMatch = errorMessage.match(/(?:Error|KeyError):\s*'?([a-z]+:[^'\s,)]+)'?/) ||
                             errorMessage.match(/\b(smiles|chembl|unichem|pubchem|uniprot|ensembl):[^\s'"`,)]+/i);

    if (entityErrorMatch) {
      errorType = "entity-error";
      fingerprint = ["llm-error", "entity-error"];
      entityId = entityErrorMatch[1] || entityErrorMatch[0];

      // Determine entity type from ID prefix
      if (entityId.startsWith("smiles:")) {
        entityType = "compound";
        fingerprint.push("compound");
      } else if (entityId.startsWith("chembl:") || entityId.startsWith("unichem:") || entityId.startsWith("pubchem:")) {
        entityType = "compound";
        fingerprint.push("compound");
      } else if (entityId.startsWith("uniprot:") || entityId.startsWith("ensembl:")) {
        entityType = "target";
        fingerprint.push("target");
      } else {
        fingerprint.push("unknown-entity-type");
      }
    } else if (errorMessage.includes("timeout")) {
      errorType = "timeout";
      fingerprint.push("timeout");
    } else if (errorMessage.includes("attachment") || errorMessage.includes("attached file")) {
      errorType = "attachment";
      fingerprint.push("attachment-error");
    } else if (errorMessage.includes("duplicate document names")) {
      errorType = "duplicate-documents";
      fingerprint.push("duplicate-document-names");
    } else if (errorMessage.includes("ValidationException")) {
      errorType = "validation";
      fingerprint.push("bedrock-validation");
    } else if (errorMessage.includes("graph does not match") || errorMessage.includes("Not all IDs found")) {
      errorType = "data-inconsistency";
      fingerprint = ["llm-error", "data-inconsistency"];
    } else if (error?.status === 400 || errorMessage.includes("Invalid API call")) {
      errorType = "bad-request";
      fingerprint = ["llm-error", "bad-request"];
    } else if (error?.name === "NotAuthorized" || errorMessage.includes("Token refresh failed") ||
               errorMessage.includes("Token refresh timed out") ||
               errorMessage.includes("same token returned") || errorMessage.includes("Max auth retries exceeded") ||
               errorMessage.includes("Already failed once with this token")) {
      errorType = "session-expired";
      fingerprint = ["llm-error", "session-expired"];
    } else {
      fingerprint.push("unknown");
    }

    const transientTypes = new Set(["generic", "timeout"]);
    const level = transientTypes.has(errorType) && !this.active ? "warning" : "error";

    const sentryOpts = {
      level,
      asException: true,
      errorName: errorMessage.substring(0, 120),
      captureStackTrace: false,
      fingerprint,
      tags: {
        errorType,
        streamId: this.streamId,
        agentId: this.agentId,
        active: this.active,
        sessionAgent: this.sessionAgent,
        pollingMode: this.pollingMode,
        ...(entityId && { entityId }),
        ...(entityType && { entityType }),
        ...extraTags
      },
      ...this._buildConversationCapture({ error, timestamp }),
    };

    if (includeEventsSnapshot && this.streamId) {
      // Capture server-side SSE event queue as extra context on this Sentry event.
      // Must actually WAIT for the snapshot response before nulling streamId: nulling
      // streamId synchronously triggers _cancelAgents (below), which POSTs a cancel
      // that the backend processes almost immediately (channel_clear()/close() empty
      // the Redis queue this snapshot reads). fetch() is async and does not block the
      // next line, so nulling it right after merely *starting* the fetch let the cancel
      // race ahead of the snapshot on every real occurrence — the snapshot GET landed
      // server-side after the queue was already torn down, making `snapshot.events`
      // near-empty on every past capture (confirmed via WEBAPP-3S/PLEX-6310: server
      // event_count and client clientEvents both showed just 1 stale entry). Holding
      // the null/cancel until the snapshot settles fixes that without changing when
      // the user-visible error/cancel happens by more than one network round trip.
      const clientEvents = this._clientEventLog.slice();
      // PLEX-6310: read-loop health at fire time, from useStreamConnector — captured
      // synchronously (unlike snapshot/clientEvents, no fetch/race needed) so it
      // reflects the exact instant the watchdog fired. See recordTick's comment in
      // useStreamConnector.js: this exists specifically because a real incident left
      // both server-side snapshot and client console breadcrumbs uninformative, and
      // this is independent of both.
      const streamLoopHealth = this._getStreamLoopHealth?.() ?? null;
      const headers = this.auth?.jwtToken ? { Authorization: `Bearer ${this.auth.jwtToken}` } : {};
      // Bounded by a race against `wait`: a hung/slow snapshot endpoint must not delay
      // the user-visible cancel indefinitely. On timeout, snapshot is just omitted —
      // the cancel/error path still proceeds normally.
      Promise.race([
        fetch(`/api/stream/${this.streamId}/snapshot`, { headers })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null),
        wait(3000).then(() => null),
      ])
        .then(snapshot => {
          captureMessage(errorMessage, {
            ...sentryOpts,
            extra: { ...sentryOpts.extra, snapshot: snapshot || null, clientEvents, streamLoopHealth },
          });
          this.streamId = null;
        });
    } else {
      captureMessage(errorMessage, sentryOpts);
      this.streamId = null;
    }

    // Create user-friendly error message
    let userMessage = errorMessage;
    if (errorType === "entity-error" && entityId) {
      const shortId = entityId.length > 80 ? entityId.substring(0, 77) + "..." : entityId;
      userMessage = `Unable to process ${entityType || "entity"}: ${shortId}\n\n` +
                    `This may indicate:\n\n` +
                    `- The ${entityType || "entity"} is not in the Plex database\n` +
                    `- The identifier may be malformed or invalid\n` +
                    `- There is no biological data available for this ${entityType || "entity"}\n\n` +
                    `Try:\n\n` +
                    (entityType === "compound"
                      ? `- Searching for a similar compound using structure search\n` +
                        `- Using a different identifier (ChEMBL ID, compound name)\n` +
                        `- Verifying the SMILES string is correctly formatted`
                      : `- Searching for a related ${entityType || "entity"}\n` +
                        `- Using an alternative identifier\n` +
                        `- Checking the identifier format`);
    } else if (errorType === "attachment") {
      userMessage = "There was an issue with an attached file. Please try re-attaching the file.";
    } else if (errorType === "duplicate-documents") {
      userMessage = "Cannot send duplicate documents. Please remove duplicate attachments.";
    } else if (errorType === "data-inconsistency") {
      userMessage = "Data inconsistency detected in the search results.\n\n" +
                    "This is a known issue that occurs when:\n\n" +
                    "- A compound exists in search results but not in the graph database\n" +
                    "- There is a temporary sync issue between data sources\n\n" +
                    "The backend team has been notified. Please try:\n\n" +
                    "- Refreshing your search\n" +
                    "- Using a different compound from the results";
    } else if (errorType === "bad-request") {
      userMessage = "An error occurred. Please try again.";
    } else if (errorType === "session-expired") {
      userMessage = "Your session has expired. Please sign in again.";
      if (this._userStore) {
        this._userStore.tokenExpired = true;
      }
    }

    this.turns.push({
                      role: ROLE_SYSTEM,
                      content: [{text: `[${timestamp}] ${userMessage}`}],
                      status: "error",
                      stopReason: "error",
                      error,
                      ts: Date.now()});
  }

  _tagMissingAttachment = (turns, errorMessage) => {
    // Check if this is an attachment-related error and tag the problematic attachments
    const isNotFoundError = errorMessage.includes("Attachment") && errorMessage.includes("not found");
    const isAccessDeniedError = errorMessage.includes("Access denied to attachment");

    if (isNotFoundError || isAccessDeniedError) {
      // Extract key/filename from error messages like:
      // "Attachment 'filename.pdf' not found"
      // "Access denied to attachment at key `...': The attachment may not exist..."
      let identifier = null;
      let errorType = null;
      let userMessage = null;

      if (isAccessDeniedError) {
        // Extract key from "Access denied to attachment at key `<key>`"
        const keyMatch = errorMessage.match(/at key [`']([^`']+)[`']/);
        identifier = keyMatch ? keyMatch[1] : null;
        errorType = "access_denied";
        userMessage = "Error reading attachment, please re-attach";
      } else {
        // Extract filename from "Attachment 'filename.pdf' not found"
        const filenameMatch = errorMessage.match(/Attachment '([^']+)'/);
        identifier = filenameMatch ? filenameMatch[1] : null;
        errorType = "not_found";
        userMessage = "Attachment not found, please re-attach";
      }

      if (identifier) {
        // Find and tag the attachment in previous turns
        for (let i = turns.length - 1; i >= 0; i--) {
          const turn = turns[i];
          if (turn.role === ROLE_USER && turn.content) {
            let found = false;
            turn.content.forEach(block => {
              const attachmentBlock = block.document || block.image;
              if (attachmentBlock?.source?.file) {
                const file = attachmentBlock.source.file;
                // Match by name or uploadId/key
                if (file.name === identifier || file.uploadId === identifier) {
                  attachmentBlock.error = errorType;
                  attachmentBlock.errorMessage = userMessage;
                  found = true;
                }
              }
            });
            if (found) break; // Stop after finding the attachment
          }
        }
      }
    }
  }

  // Sentry's default data scrubber ("@password:filter" etc.) scans string VALUES
  // for key:value-shaped substrings, not just structured object keys. Once the
  // snapshot below is JSON.stringify'd to a single string (needed to survive
  // normalizeDepth), one matching substring anywhere in the ~30-turn window
  // causes Sentry to replace the ENTIRE string with "[Filtered]" — silently
  // destroying the forensic snapshot this capture exists to provide (WEBAPP-B2).
  // Pre-redact known-sensitive keys ourselves so nothing in the surviving JSON
  // can trigger that blanket filter. This must also scrub leaf string VALUES
  // (e.g. LLM message/tool-call text that echoes "Authorization: Bearer ...")
  // since Sentry's scrubber matches those regardless of the surrounding key —
  // redacting only object keys (as the original WEBAPP-B2 fix did) still leaves
  // that path open and the whole snapshot still collapses to "[Filtered]".
  _SENSITIVE_KEY_RE = /password|secret|token|api[-_]?key|authorization|credential/i;
  _SENSITIVE_VALUE_RE = /(password|secret|token|api[-_]?key|authorization|credential)(\s*[:=]\s*)(?:bearer\s+)?\S+/gi;

  _redactSensitiveKeys = (value) => {
    if (Array.isArray(value)) {
      return value.map(this._redactSensitiveKeys);
    }
    if (value && typeof value === "object") {
      const out = {};
      for (const [key, val] of Object.entries(value)) {
        out[key] = this._SENSITIVE_KEY_RE.test(key) ? "[redacted]" : this._redactSensitiveKeys(val);
      }
      return out;
    }
    if (typeof value === "string") {
      return value.replace(this._SENSITIVE_VALUE_RE, "$1$2[redacted]");
    }
    return value;
  }

  _serializeEvent = (event) => {
    // Serialize SSE event to avoid "[object Object]" in Sentry logs
    const data = (event.data ? safeParseJSON(event.data) : null) || {};
    return {
      type: event.type,
      lastEventId: event.lastEventId,
      data: data,
      timestamp: event.timeStamp
    };
  }

  _captureStreamAnomaly = (message, sentryOpts) => {
    // Fetch the backend snapshot first, then emit a single Sentry event with both
    // the anomaly details and the server-side event queue for forensic correlation.
    // clientEvents (from _clientEventLog) lets us diff what the client received vs
    // what the server published.
    const clientEvents = this._clientEventLog.slice();
    if (!this.streamId) {
      captureMessage(message, { ...sentryOpts, extra: { ...sentryOpts?.extra, clientEvents } });
      return;
    }
    const headers = this.auth?.jwtToken ? { Authorization: `Bearer ${this.auth.jwtToken}` } : {};
    // Named (not inline-anonymous) so Sentry stack traces show a real function
    // name here instead of "<anonymous>" for captureMessage calls raised from
    // this callback (see WEBAPP-8R / WEBAPP-8X).
    const captureWithSnapshot = snapshot => {
      // Keep only events around the anomaly: anchor on the last message_start in the
      // server queue (the errant event), then retain ±2 full turns (~30 events each
      // side). Events outside that window are replaced with stubs so Sentry's payload
      // limit is respected while index context is preserved.
      let snapshotExtra = snapshot || null;
      if (snapshot?.events?.length > 60) {
        const events = snapshot.events;
        // Find the last message_start in the queue as the anchor point
        let center = events.length - 1;
        for (let i = events.length - 1; i >= 0; i--) {
          if (events[i]?.type === "message_start") { center = i; break; }
        }
        const WINDOW = 30;
        const start = Math.max(0, center - WINDOW);
        const end = Math.min(events.length, center + WINDOW + 1);
        const trimmed = [
          ...(start > 0 ? [{ _stub: true, skipped: start }] : []),
          ...events.slice(start, end),
          ...(end < events.length ? [{ _stub: true, skipped: events.length - end }] : []),
        ];
        snapshotExtra = { ...snapshot, events: trimmed, truncated: true };
      }
      // Serialize snapshot to a JSON string: Sentry's normalizeDepth=3 would
      // otherwise flatten nested event objects (content_block, delta, etc.) to
      // "[Object]", making the snapshot unreadable. A string bypasses that limit.
      const snapshotStr = snapshotExtra ? JSON.stringify(this._redactSensitiveKeys(snapshotExtra)) : null;
      captureMessage(message, {
        ...sentryOpts,
        extra: { ...sentryOpts?.extra, snapshot: snapshotStr, clientEvents },
      });
    };
    fetch(`/api/stream/${this.streamId}/snapshot`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(captureWithSnapshot)
      .catch(() => captureMessage(message, { ...sentryOpts, extra: { ...sentryOpts?.extra, clientEvents } }));
  }

  _handleStreamError = (turns, index, event, messageText = null) => {
    // console.debug, not .warn: Sentry's captureConsoleIntegration captures
    // "warn"/"error"/"assert" levels and turns each call into its own Sentry
    // issue. This diagnostic line embeds index/mainAgentId, so every call
    // produced near-unique text Sentry couldn't group — spawning a fresh
    // noise issue per stream error (e.g. WEBAPP-DJ) alongside the real,
    // properly-classified captureMessage() call a few lines below.
    console.debug(
      `[LLMClient] _handleStreamError closing root stream: index=${index} messageText=${JSON.stringify((messageText || "").slice(0, 150))} ` +
      `eventType=${event?.type} eventAgentId=${event?.data ? (() => { try { return JSON.parse(event.data)?.agent_id; } catch { return undefined; } })() : undefined} mainAgentId=${this.agentId}`
    );
    // Capture partial state for forensics BEFORE cleaning it up
    const lastTurn = turns[index];
    const forensicData = {
      index,
      event: this._serializeEvent(event),
      partialState: null
    };

    if (lastTurn?.partial) {
      // Save the partial turn state for debugging
      forensicData.partialState = {
        role: lastTurn.role,
        partial: true,
        contentBlocks: lastTurn.content.map((block, i) => ({
          index: i,
          partial: !!block.partial,
          type: block.text ? 'text' : block.toolUse ? 'toolUse' : block.toolResult ? 'toolResult' : 'unknown',
          preview: block.text?.substring(0, 100) || JSON.stringify(block).substring(0, 100)
        }))
      };

      // Clean up the partial flags so conversation can continue
      delete lastTurn.partial;
      lastTurn.content.forEach(block => {
        if (block.partial) {
          delete block.partial;
        }
      });

      // Append a truncation notice to the last text block so the user knows
      // the message was cut short (covers network drops and server exceptions).
      // This is visible to all users; the separate error turn below carries
      // full detail for admins only.
      const lastTextBlock = [...lastTurn.content].reverse().find(b => b.text != null);
      if (lastTextBlock) {
        lastTextBlock.text += "\n\n*(Response was interrupted)*";
      } else {
        lastTurn.content.push({ text: "*(Response was interrupted)*" });
      }
    }

    // terminate streaming and register the error
    const timestamp = new Date().toISOString();
    const data = (event.data ? safeParseJSON(event.data) : null) || {};
    const eventType = data.type || event.type;
    if (!messageText) {
      messageText = `Improper agent message ${data.agent_id}:${eventType} for '${turns[index]?.role ?? 'unknown'}' at message index ${index}`;
    }

    // Create better Sentry title and fingerprint based on error type
    const connectionState = event.connectionState;
    let sentryTitle = messageText;
    let sentryFingerprint = ["llm-stream-error", eventType || "unknown"];
    let errorCategory = "unknown";
    let entityId = null;
    let entityType = null;

    // Extract server error message early to check for entity errors
    const serverErrorMessage = data.error || data.content?.map(b => b.text || "").join("") || null;

    // Check for entity-related errors in server message or event data
    const entityErrorMatch = (serverErrorMessage || messageText).match(/(?:Error|KeyError):\s*'?([a-z]+:[^'\s,)]+)'?/) ||
                             (serverErrorMessage || messageText).match(/No valid nodes within \[?'?([a-z]+:[^'\s,\])]+)/) ||
                             (serverErrorMessage || messageText).match(/\b(smiles|chembl|unichem|pubchem|uniprot|ensembl):[^\s'"`,)]+/i);

    if (entityErrorMatch) {
      errorCategory = "entity-error";
      entityId = entityErrorMatch[1] || entityErrorMatch[0];
      sentryTitle = `LLM streaming: Entity processing error (${entityId.substring(0, 50)}...)`;
      sentryFingerprint = ["llm-stream-error", "entity-error"];

      // Determine entity type
      if (entityId.startsWith("smiles:") || entityId.startsWith("chembl:") ||
          entityId.startsWith("unichem:") || entityId.startsWith("pubchem:")) {
        entityType = "compound";
        sentryFingerprint.push("compound");
      } else if (entityId.startsWith("uniprot:") || entityId.startsWith("ensembl:")) {
        entityType = "target";
        sentryFingerprint.push("target");
      }
    } else if ((serverErrorMessage || messageText).includes("graph does not match") ||
               (serverErrorMessage || messageText).includes("Not all IDs found")) {
      errorCategory = "data-inconsistency";
      sentryTitle = "LLM streaming: Data inconsistency error";
      sentryFingerprint = ["llm-stream-error", "data-inconsistency"];

      // Try to extract entity ID from "Not all IDs found for compound X/Y, graph does not match ES (['smiles:...'])"
      const graphMismatchMatch = (serverErrorMessage || messageText).match(/\(\['([^']+)'\]\)/);
      if (graphMismatchMatch) {
        entityId = graphMismatchMatch[1];
        entityType = "compound";
      }
    } else if (event.isTimeout) {
      // Client-side timeout - no data received for extended period
      errorCategory = "client-timeout";
      sentryTitle = "LLM streaming: Client timeout waiting for data (SSE)";
      sentryFingerprint = ["llm-stream-error", "client-timeout", connectionState];
    } else if (connectionState === "CONNECTING") {
      // Connection failed before establishment - likely server error (500, timeout, etc)
      errorCategory = "connection-failed";
      sentryTitle = "LLM streaming: Connection failed to establish (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-failed", connectionState];

      // Try to detect if this is a server error vs network error
      if (event.connectionDuration && event.connectionDuration < 5000) {
        // Fast failure likely means server rejected (500, 503, etc)
        sentryTitle = "LLM streaming: Server error during connection (HTTP 500/503)";
        errorCategory = "server-error";
        sentryFingerprint = ["llm-stream-error", "server-error", connectionState];
      }
    } else if (connectionState === "OPEN") {
      // Connection was open but then failed - likely network interruption or server crash
      errorCategory = "connection-interrupted";
      sentryTitle = "LLM streaming: Connection interrupted mid-stream (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-interrupted", connectionState];
    } else if (connectionState === "CLOSED") {
      // Connection was already closed
      errorCategory = "connection-closed";
      sentryTitle = "LLM streaming: Connection already closed (SSE)";
      sentryFingerprint = ["llm-stream-error", "connection-closed", connectionState];
    } else if (messageText.includes("Improper agent message")) {
      // Protocol error - malformed SSE message
      errorCategory = "protocol-error";
      sentryTitle = "LLM streaming: Malformed SSE message from server";
      sentryFingerprint = ["llm-stream-error", "protocol-error", eventType || "unknown"];
    } else if (messageText.includes("Unexpected event")) {
      // Unexpected event type
      errorCategory = "unexpected-event";
      sentryTitle = "LLM streaming: Unexpected event type from server";
      sentryFingerprint = ["llm-stream-error", "unexpected-event", eventType || "unknown"];
    } else if (messageText.includes("attachment") || messageText.includes("attached file")) {
      // Attachment-related error
      errorCategory = "attachment-error";
      sentryTitle = "LLM streaming: Attachment error";
      sentryFingerprint = ["llm-stream-error", "attachment-error"];
    } else if (messageText.includes("duplicate document names")) {
      // Duplicate document names (should be prevented by frontend now)
      errorCategory = "duplicate-documents";
      sentryTitle = "LLM streaming: Duplicate document names";
      sentryFingerprint = ["llm-stream-error", "duplicate-documents"];
    }

    // Build comprehensive error context message
    let contextMessage = messageText;
    let userMessage = messageText; // User-friendly version of the error

    // Create user-friendly message for entity errors
    if (errorCategory === "entity-error" && entityId) {
      const shortId = entityId.length > 80 ? entityId.substring(0, 77) + "..." : entityId;
      userMessage = `Unable to process ${entityType || "entity"}: ${shortId}\n\n` +
                    `This may indicate:\n\n` +
                    `- The ${entityType || "entity"} is not in the Plex database\n` +
                    `- The identifier may be malformed or invalid\n` +
                    `- There is no biological data available for this ${entityType || "entity"}\n\n` +
                    `Try:\n\n` +
                    (entityType === "compound"
                      ? `- Searching for a similar compound using structure search\n` +
                        `- Using a different identifier (ChEMBL ID, compound name)\n` +
                        `- Verifying the SMILES string is correctly formatted`
                      : `- Searching for a related ${entityType || "entity"}\n` +
                        `- Using an alternative identifier\n` +
                        `- Checking the identifier format`);
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage || "entity error"}`;
    } else if (errorCategory === "data-inconsistency") {
      userMessage = "Data inconsistency detected in the search results.\n\n" +
                    "This is a known issue that occurs when:\n\n" +
                    "- A compound exists in search results but not in the graph database\n" +
                    "- There is a temporary sync issue between data sources\n\n" +
                    "The backend team has been notified. Please try:\n\n" +
                    "- Refreshing your search\n" +
                    "- Using a different compound from the results";
      if (entityId) {
        userMessage += `\n\nProblematic entity: ${entityId}`;
      }
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage || "data inconsistency"}`;
    } else if (serverErrorMessage && errorCategory !== "entity-error" && errorCategory !== "data-inconsistency") {
      errorCategory = "backend-error";
      contextMessage = `${messageText}\nServer message: ${serverErrorMessage}`;
      sentryTitle = `LLM streaming: ${serverErrorMessage.substring(0, 100)}`;
      userMessage = serverErrorMessage; // Show server's error message to user

      // Do NOT set a custom sentryFingerprint here. This fallback covers
      // arbitrary, uncategorized backend error messages (see WEBAPP-7Y:
      // "Connection lost", entity-lookup failures, and unrelated Python
      // exceptions all merged into one Sentry issue under the static
      // ["llm-stream-error", eventType] fingerprint, since eventType is
      // almost always "error"). Clear sentryFingerprint (set to that static
      // default at declaration above) so Sentry falls back to its default
      // message-based grouping, which splits by the actual error text
      // instead of lumping every uncategorized backend error together.
      sentryFingerprint = undefined;
    } else if (connectionState === "CONNECTING" && !serverErrorMessage) {
      contextMessage = `${messageText}\nNote: Server returned error without descriptive message. Backend should provide error details in SSE event data.`;
      // Create user-friendly message based on error category
      if (errorCategory === "server-error") {
        userMessage = "Server error (HTTP 500/503). Please try again.";
      } else if (errorCategory === "connection-failed") {
        userMessage = "Connection failed. Please check your network and try again.";
      } else if (errorCategory === "client-timeout") {
        const minutes = Math.round((event.timeSinceLastData || 0) / 60000);
        userMessage = `Connection timeout after ${minutes} minute${minutes !== 1 ? 's' : ''} with no response. The server may be experiencing issues. Please try again.`;
      }
    } else if (event.isTimeout) {
      // Handle timeout in other connection states
      const minutes = Math.round((event.timeSinceLastData || 0) / 60000);
      contextMessage = `${messageText}\nConnection timed out after ${minutes} minutes with no data received.`;
      userMessage = `Connection timeout after ${minutes} minute${minutes !== 1 ? 's' : ''} with no response. Please try again.`;
    }

    const conversationCapture = this._buildConversationCapture({ forensicData, timestamp });
    captureMessage(sentryTitle, {
      level: (errorCategory === "connection-closed" || errorCategory === "connection-interrupted") && !this.active ? "warning" : "error",
      asException: true,
      errorName: sentryTitle.substring(0, 120),
      captureStackTrace: false,
      fingerprint: sentryFingerprint,
      tags: {
        eventType,
        agentId: data.agent_id,
        mainAgentId: this.agentId,
        streamId: this.streamId,
        active: this.active,
        sessionAgent: this.sessionAgent,
        connectionState,
        errorCategory,
        hasServerMessage: !!serverErrorMessage,
        connectionDuration: event.connectionDuration,
        isTimeout: !!event.isTimeout,
        timeSinceLastData: event.timeSinceLastData,
        ...(event.status != null && { status: event.status }),
        ...(entityId && { entityId }),
        ...(entityType && { entityType })
      },
      context: {
        error_details: {
          originalMessage: messageText,
          serverMessage: serverErrorMessage,
          fullContext: contextMessage,
          connectionState,
          eventType,
          url: event.url,
          connectionDuration: event.connectionDuration,
          status: event.status,
          ...(entityId && { entityId }),
          ...(entityType && { entityType })
        },
        // PLEX-6110/WEBAPP-7G: only present on the duplicate-message_start
        // ("protocol-error") branch — raw SSE event history + this agent's
        // own turn tail, to pin down whether an intervening toolResult
        // event was dropped/misrouted during a burst-catchup.
        ...(event._duplicateStartDebug && { duplicate_start_debug: event._duplicateStartDebug }),
        ...conversationCapture.context,
      },
      attachments: conversationCapture.attachments || [],
    });
    this.streamId = null;

    // Tag any missing attachments mentioned in the error
    this._tagMissingAttachment(turns, messageText);

    const systemError = {
      role: ROLE_SYSTEM,
      status: "error",
      stopReason: "error",
      content: [{text: `[${timestamp}] ${userMessage}`}],
      ts: Date.now(),
      details: forensicData
    };

    // Check if we need to add an ASSISTANT turn to maintain alternation
    // Look at the last non-SYSTEM turn
    const lastNonSystemTurn = turns.slice().reverse().find(t => t != null && t.role !== ROLE_SYSTEM);
    const isAdmin = this._userStore?.isAdmin || false;

    // If the last assistant turn has unpaired toolUse blocks, inject a toolResult turn
    // so the conversation stays structurally valid for resume. Without this, the frontend
    // stores a role=system error turn that the backend has to strip, but the toolUse
    // remains unpaired and causes Bedrock ValidationException on the next request.
    const unpairedToolUseIds = (lastNonSystemTurn?.role === ROLE_ASSISTANT)
      ? (lastNonSystemTurn.content || [])
          .filter(b => b.toolUse)
          .map(b => b.toolUse.toolUseId)
          .filter(id => {
            // Check if there's already a toolResult for this id in any following turn
            const idx = turns.indexOf(lastNonSystemTurn);
            return !turns.slice(idx + 1).some(t =>
              (t.content || []).some(b => b.toolResult?.toolUseId === id)
            );
          })
      : [];

    if (unpairedToolUseIds.length > 0) {
      // Inject a user turn with error toolResults covering every unpaired toolUse
      turns.push({
        role: ROLE_USER,
        content: unpairedToolUseIds.map(id => ({
          toolResult: {
            toolUseId: id,
            content: [{text: `Tool call failed: ${userMessage}`}],
            status: "error"
          }
        })),
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      // Follow with an assistant error acknowledgment to keep alternation intact
      turns.push({
        role: ROLE_ASSISTANT,
        content: [{ text: "I'm sorry, I ran into an error and wasn't able to complete that request. Please try again." }],
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      if (isAdmin) {
        // Append system error for admin technical detail (doesn't affect API turn structure)
        turns.push(systemError);
      }
      return isAdmin ? systemError : null;
    } else if (lastNonSystemTurn?.role === ROLE_USER) {
      // Last message was USER, so next should be ASSISTANT
      // For non-admins: Only add ASSISTANT error acknowledgment (skip SYSTEM error as it's redundant)
      // For admins: Add both SYSTEM error (for technical details) and ASSISTANT acknowledgment
      if (isAdmin) {
        turns.push(systemError);
      }
      turns.push({
        role: ROLE_ASSISTANT,
        content: [{ text: "I'm sorry, I ran into an error and wasn't able to complete that request. The AI service may be temporarily unavailable. Please try again in a moment." }],
        stopReason: "error",
        status: "error",
        ts: Date.now()
      });
      // For non-admins, return null to prevent duplicate error messages at call sites
      // For admins, return systemError so they see technical details
      return isAdmin ? systemError : null;
    } else {
      // Last message was ASSISTANT (or no messages), just add SYSTEM error
      turns.push(systemError);
      return systemError;
    }
  }

  handleStreamEvent = (event) => {

    // NOTE: the streaming message types handled here correspond to the results
    // of bedrock's converse_stream API
    const eventData = (event.data ? safeParseJSON(event.data) : null) || {};
    const mtype = eventData?.type || event.type;
    const roleInEvent = 'role' in eventData;
    const {role = ROLE_SYSTEM, agent = this.sessionAgent, agent_id: agentId = this.agentId} = eventData;
    const stopReason = eventData.stop_reason || eventData.stopReason;
    console.debug(`handleStreamingResponse ${mtype}`, event);
    // Only log main-agent events: sub-agent events flood the 200-entry cap and
    // crowd out the main-agent context needed for anomaly diagnosis. Sub-agent
    // event history is available via the server-side SSE snapshot in Sentry.
    //
    // PLEX-6310: this used to stop appending once length reached 200 (first-N,
    // never rotated). A dense early burst of content_block_delta events (the
    // guide's first response, before it even dispatches to a sub-agent) fills
    // 200 slots in single-digit seconds on every real session, so by the time a
    // watchdog fires minutes later the log has been dark for the entire silent
    // stretch that actually matters — confirmed on whayes' own capture: 177
    // entries spanning 9s of an 8-minute session, nothing from message ~9s
    // onward. Rotate instead: keep the most recent 200 so a timeout capture
    // actually covers the moments leading up to it.
    if (agentId === this.agentId) {
      this._clientEventLog.push({mtype, role, agentId, ts: event.timeStamp});
      if (this._clientEventLog.length > 200) {
        this._clientEventLog.shift();
      }
    }

    if (!this.streamId && !this.active) {
      // Terminal events from sub-agents (message_stop, ping) arriving after the parent
      // stream closes are expected and harmless — swallow them silently.
      const TERMINAL_EVENTS = ["message_stop", "ping"];
      if (TERMINAL_EVENTS.includes(mtype)) {
        console.debug(`[LLMClient] Stale terminal event ignored: mtype=${mtype} agentId=${agentId}`);
        return;
      }
      // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): embeds
      // agentId/streamId per call, so Sentry's captureConsoleIntegration
      // can't group occurrences and spawns a fresh noise issue each time.
      console.debug(`[LLMClient] Stale event guard fired: mtype=${mtype} agentId=${agentId} role=${role} streamId=${this.streamId} active=${this.active}`, event, eventData);
      if (!this._staleEventReported) {
        this._staleEventReported = true;
        Analytics.event(Analytics.EVENTS.CHAT_STALE_EVENT, { mtype, agentId, role });
      }
      cancelAgent(agentId, this.auth).then(() => {});
      return;
    }

    // Pre-populate agent map for any event arriving for a sub-agent before its
    // toolResult block initialises the entry (race condition in multi-agent streaming).
    // Wrapped in observable.array explicitly (the map itself is observable.shallow) —
    // only actively-streaming sub-agents need MobX tracking for UI reactivity.
    if (agentId && agentId !== this.agentId && !this.subAgentTurns.has(agentId)) {
      this.subAgentTurns.set(agentId, observable.array([]));
    }

    const turns = agentId === null || agentId === this.agentId
                  ? this.turns
                  : this.subAgentTurns.get(agentId);
    if (!turns) {
      // Should be unreachable after the pre-population above; kept as a silent fallback.
      // Client-side dedup: at most one Sentry event per (streamId, agentId) pair.
      // Fixed fingerprint aggregates all occurrences into a single Sentry issue;
      // agent type and agentId are available as tags for filtering.
      const missingKey = `${this.streamId}:${agentId}`;
      if (!this._reportedMissingAgents.has(missingKey)) {
        this._reportedMissingAgents.add(missingKey);
        captureMessage(`Turns missing for agent`, {
          level: "warning",
          fingerprint: ["llm-turns-missing"],
          tags: {
            requestedAgentId: agentId,
            mainAgentId: this.agentId,
            streamId: this.streamId,
            eventType: mtype,
            agent: agent
          },
        });
      }
      return;
    }
    const lastMessage = turns.length ? turns.at(-1) : null;
    const lastMessageIndex = turns.length ? turns.length - 1 : null;
    // The synthetic search-status turn (role=system, not partial, no LLM content) is
    // a UI sentinel, not an LLM protocol turn. Protocol events should not validate
    // against it — treat it as absent for role/partial checks.
    const lastLLMMessage = (lastMessage?.role === ROLE_SYSTEM && !lastMessage?.partial) ? null : lastMessage;
    const lastLLMMessageIndex = lastLLMMessage ? lastMessageIndex : null;

    const msgId = `${this.streamId?.slice(0, 4)}:${event.lastEventId?.slice(0, 4) || "error"}`;
    const context = `${agent}:${agentId?.slice(-4)}:${role}:${mtype}`;
    console.debug(`Received ${msgId} => ${context} (convo length: ${turns.length})`, eventData);

    if (mtype === "search_update" || mtype === "search_results") {
      const systemTurnIdx = turns.findIndex(t => t != null && t.role === ROLE_SYSTEM && !t.partial);
      // progress is a SearchProgress int (0=SPAWNING … 8=COMPLETE); total is safe to display.
      // info text is captured verbatim for display — backend should sanitize before sending.
      const SEARCH_STAGE_LABELS = {
        0: "Searching with Plex…",        // SPAWNING
        1: "Searching with Plex…",        // INITIALIZING
        2: "Running similarity search…",  // SIMILARITIES
        3: "Expanding results…",          // EXPANSION
        4: "Ranking results…",            // RANKING
        5: "Analyzing communities…",      // COMMUNITIES
        6: "Finding connections…",        // CONNECTIONS
        7: "Finalizing results…",         // TAGGING
        8: "Search complete",             // COMPLETE
      };
      const total = eventData.total;
      const progressStage = eventData.progress;
      const progressInfo = eventData.info;
      const stageLabel = SEARCH_STAGE_LABELS[progressStage] ?? "Searching with Plex…";
      const countText = total != null && total > 0 ? ` — ${total} result${total !== 1 ? "s" : ""} found` : "";
      const displayText = `${stageLabel}${countText}`;
      if (systemTurnIdx >= 0) {
        const prev = turns[systemTurnIdx];
        // Only replace (triggers MobX re-render) when something meaningful changed
        if (progressInfo !== prev._lastInfo || total !== prev._total || progressStage !== prev._progress) {
          turns.splice(systemTurnIdx, 1, {
            ...prev,
            content: [{ text: displayText }],
            _lastInfo: progressInfo,
            _total: total,
            _progress: progressStage,
          });
        }
      } else {
        turns.push({
          role: ROLE_SYSTEM,
          content: [{ text: displayText }],
          ts: Date.now(),
          _lastInfo: progressInfo,
          _total: total,
          _progress: progressStage,
        });
      }
    }
    else if (mtype === "message_start") {
      // message_start may or may not include information beyond simply "role"
      //{"type": "message_start","message": {"id": "msg_01GnRNG1psEzTMECgZr32tiv","content": [],"model": "claude-3-sonnet-28k-20240229","role": "assistant","stop_reason": null,"stop_sequence": null,"type": "message","usage": {"input_tokens": 11414,"output_tokens": 1}}}
      // For debugging, keep track of the tool use IDs we're expecting
      if (lastLLMMessage && eventData.role === lastLLMMessage?.role) {
        // Duplicate message_start for the same role — genuine protocol error.
        // PLEX-6110/WEBAPP-7G instrumentation: this fires both for real backend
        // protocol errors AND for a suspected burst-catchup bug where an
        // intervening toolResult turn gets dropped/misrouted. Capture the raw
        // SSE event history (from the EventSource's ring buffer) plus the tail
        // of this agent's own turn history, so a future occurrence shows
        // whether the toolResult event was ever actually delivered.
        event._duplicateStartDebug = {
          agentId,
          recentRawEvents: event?.srcElement?.getRecentEvents?.() || null,
          turnsTail: turns.slice(-6).map((t, i) => ({
            idx: turns.length - 6 + i,
            role: t?.role,
            partial: !!t?.partial,
            stopReason: t?.stopReason,
            contentBlockTypes: (t?.content || []).map(b =>
              b.toolResult ? "toolResult" : b.toolUse ? "toolUse" : b.text != null ? "text" : "unknown"),
          })),
        };
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
      else {
        if (lastLLMMessage?.partial) {
          // Previous turn still open on role-switch — message_stop was not received before
          // the next message_start. Close the stale partial turn so the conversation state
          // machine stays consistent; the new turn opens normally below.
          delete lastLLMMessage.partial;
          cleanTurn(lastLLMMessage);
          this._captureStreamAnomaly(
            `message_start on partial turn: role switching ${lastLLMMessage.role}→${eventData.role}`,
            { level: "warning", extra: { incomingRole: eventData.role, incomingAgentId: agentId } }
          );
        }
        const newTurn = {
          role: eventData.role,
          ts: Date.now(),
          content: [],
          partial: true
        }
        if (agentId) {
          newTurn.agentId = agentId;
        }
        if (eventData.role === ROLE_ASSISTANT) {
          Analytics.event(Analytics.EVENTS.AGENT_TURN_START, {
            agent_id: agentId,
            is_root: !agentId || agentId === this.agentId,
            stream_id: this.streamId,
            via_polling: this.pollingMode ?? false,
          });
        }
        console.debug(`Add new turn for role ${role} to convo length (${turns.length})`, newTurn);
        turns.push(newTurn);
      }
    }
    else if (mtype === "content_block_start") {
      // NOTE: optional; content_block_delta may be sent immediately after
      // message_start
      // Parallel tool results: backend fires all content_block_start events before executing
      // any tools, so all content[] slots exist before any out-of-order delta/stop arrives.
      // Validate that we have a partial message with matching role
      // If lastMessage is not partial, we're missing a message_start event
      // If role doesn't match, the message_start had the wrong role
      // Bedrock omits role on content_block_start — only check for an open partial message.
      if (!lastLLMMessage?.partial) {
        // message_start was lost (network gap, replay offset, or out-of-order delivery).
        // Synthesize the missing turn so the stream can continue rather than hard-erroring.
        // Log to Sentry as a warning so we can track frequency without disrupting the user.
        const synthRole = roleInEvent ? role : ROLE_SYSTEM;
        this._captureStreamAnomaly(
          `content_block_start without message_start: synthesising turn for role='${synthRole}'`,
          {
            level: "warning",
            fingerprint: ["missing-message-start", "content_block_start"],
            tags: { role: synthRole, agentId, mainAgentId: this.agentId, streamId: this.streamId },
          }
        );
        const synthTurn = { role: synthRole, ts: Date.now(), content: [], partial: true };
        if (agentId) synthTurn.agentId = agentId;
        turns.push(synthTurn);
      }
      // Re-read lastLLMMessage after possible synthesis above, skipping non-partial
      // system turns (search_update sentinels). A search_update arriving between two
      // content_block_start events would make turns.at(-1) return the sentinel instead
      // of the open partial turn, causing content[index] to be written to the wrong
      // turn and leaving it undefined on the real turn → content_block_stop error (WEBAPP-K).
      let openTurnIndex = turns.length - 1;
      while (openTurnIndex >= 0) {
        const t = turns[openTurnIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openTurnIndex--;
      }
      const openTurn = openTurnIndex >= 0 ? turns[openTurnIndex] : null;
      if (openTurn?.partial) {
        // {"content_block": {"text": "", "type": "text"}, "index": 0, "type": "content_block_start"}
        const {
          start = null,
          contentBlockIndex: index = eventData.index || 0
        } = eventData;
        if (start) {
          // Attach agent messages to the top-level turn which will contain the
          // tool result, to make the agent messages easier to filter out when
          // generating canonical messages, but also convenient for serializing
          // the entire conversation
          if (start.toolResult) {
            const { toolUseId } = start.toolResult;
            // Sub-agent events arrive before the toolResult block — reuse the existing
            // array if already populated (agent_id === tool_use_id on the backend).
            if (!this.subAgentTurns.has(toolUseId)) {
              this.subAgentTurns.set(toolUseId, observable.array([]));
            }
          }

          // Mark text blocks that come after tool use as suppressible (final answer)
          const newBlock = {
            ...start,
            partial: true
          };
          if (start.text !== undefined && openTurn.content.some(b => b.toolUse)) {
            newBlock.suppressFinalAnswer = true;
          }

          // Fill any gap below index with empty partial blocks so the array stays dense
          // and renderers never receive undefined slots (WEBAPP-9C).
          if (openTurn.content.length < index) {
            this._captureStreamAnomaly(
              `content_block_start[${index}] skipped indices ${openTurn.content.length}–${index - 1}`,
              {
                level: "warning",
                fingerprint: ["content-block-start-gap"],
                tags: { index, role: openTurn.role, agentId, mainAgentId: this.agentId,
                        streamId: this.streamId },
              }
            );
            while (openTurn.content.length < index) {
              openTurn.content.push({ partial: true });
            }
          }
          openTurn.content[index] = newBlock;
        } else {
          const error = this._handleStreamError(turns, openTurnIndex, event,
                                                `'start' block missing from content_block_start ${JSON.stringify(
                                                  eventData)}`);
          if (error) turns.push(error);
        }
      }
    }
    else if (mtype === "content_block_delta") {
      // {"delta": {"text": " that", "type": "text_delta"}, "index": 0, "type": "content_block_delta"}
      const {delta, contentBlockIndex: index = eventData.index || 0} = eventData;
      // Validate that we have a partial message with matching role
      // If lastMessage is not partial, we're missing a message_start event
      // If role doesn't match, the message_start had the wrong role
      // When role is absent from the event (roleInEvent=false), skip the role check: delta events
      // are continuations of an open message, so the role was already established by message_start.

      // Re-read the last LLM turn, skipping trailing non-partial ROLE_SYSTEM sentinels
      // (search_update progress turns). A search_update fired between content_block_start
      // and its content_block_delta inserts a system turn after the still-open partial
      // turn; without this skip, turns.at(-1) returns that sentinel and the partial check
      // false-fires as a missing-message_start anomaly (same pattern as content_block_start/
      // stop, WEBAPP-K).
      let openMsgIndex = turns.length - 1;
      while (openMsgIndex >= 0) {
        const t = turns[openMsgIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openMsgIndex--;
      }
      let openMsg = openMsgIndex >= 0 ? turns[openMsgIndex] : null;
      if (!openMsg?.partial) {
        // message_start was lost — network gap, replay offset, out-of-order delivery, or
        // a state bug that cleared the partial flag prematurely. Always capture: this is
        // never expected regardless of delta type.
        this._captureStreamAnomaly(
          `content_block_delta without message_start: synthesising turn for role='${role}'`,
          {
            level: "warning",
            fingerprint: ["missing-message-start", "content_block_delta"],
            tags: { role, agentId, mainAgentId: this.agentId, streamId: this.streamId,
                    deltaType: delta?.toolResult ? 'toolResult' : delta?.toolUse ? 'toolUse' : 'text' },
          }
        );
        const synthTurn = { role, ts: Date.now(), content: [], partial: true };
        if (agentId) synthTurn.agentId = agentId;
        turns.push(synthTurn);
        openMsg = synthTurn;
      }

      const roleOk = !roleInEvent || role === openMsg.role;
      if (!roleOk || openMsg.content[index] && !openMsg.content[index]?.partial) {
        const errorMsg = !roleOk
          ? `content_block_delta role mismatch: event role='${role}' but last message role='${openMsg.role}'`
          : `content_block_delta for non-partial content block at index ${index}`;
        const error = this._handleStreamError(turns, openMsgIndex, event, errorMsg);
        if (error) turns.push(error);
      }
      else {
        if (!openMsg.content[index]) {
          openMsg.content[index] = {};
        }
        const contentBlock = openMsg.content[index];
        contentBlock.partial = true;
        if (delta.text !== undefined) {
          contentBlock.text = (contentBlock.text || "") + delta.text;
        } else if (delta.toolUse) {
          if (contentBlock.toolUse) {
            contentBlock.toolUse.input = (contentBlock.toolUse.input || "") + delta.toolUse.input;
          } else {
            contentBlock.toolUse = delta.toolUse;
          }
        } else if (delta.toolResult) {
          contentBlock.toolResult = delta.toolResult;
        } else if (delta.reasoningContent !== undefined) {
          // Fable 5 / models with always-on thinking (Converse API): reasoningContent delta.
          // Store on the block so the UI can show a thinking indicator for responsiveness.
          if (!contentBlock.reasoningContent) contentBlock.reasoningContent = {};
          if (delta.reasoningContent.text !== undefined) {
            contentBlock.reasoningContent.text = (contentBlock.reasoningContent.text || "") + delta.reasoningContent.text;
          }
          if (delta.reasoningContent.signature !== undefined) {
            contentBlock.reasoningContent.signature = delta.reasoningContent.signature;
          }
        } else if (delta.citation !== undefined) {
          // Bedrock's CitationsDelta (RAG/grounding citations): title/source/location are
          // set/overwritten as they arrive; sourceContent is a list of text deltas that
          // accumulate like top-level text deltas do. Mirrors backend accumulation
          // (interactive.py's contentBlockDelta handling).
          if (!contentBlock.citation) contentBlock.citation = {};
          const citation = contentBlock.citation;
          if (delta.citation.title !== undefined) citation.title = delta.citation.title;
          if (delta.citation.source !== undefined) citation.source = delta.citation.source;
          if (delta.citation.location !== undefined) citation.location = delta.citation.location;
          if (delta.citation.sourceContent !== undefined) {
            if (!citation.sourceContent) citation.sourceContent = [];
            delta.citation.sourceContent.forEach((chunkDelta, i) => {
              if (citation.sourceContent[i]) {
                if (chunkDelta.text !== undefined) {
                  citation.sourceContent[i].text = (citation.sourceContent[i].text || "") + chunkDelta.text;
                }
              } else {
                citation.sourceContent[i] = { ...chunkDelta };
              }
            });
          }
        } else if (delta.image !== undefined) {
          // Bedrock's ImageBlockDelta (streaming image generation) isn't documented as
          // partial/chunked the way text is, so we don't attempt to merge — each delta is
          // treated as carrying the full image and replaces any prior image on the block.
          contentBlock.image = delta.image;
        } else {
          const error = this._handleStreamError(turns, openMsgIndex, event, "Unrecognized delta");
          if (error) turns.push(error);
        }
      }
    }
    else if (mtype === "content_block_stop") {
      // {"type": "content_block_stop", "index": 0}
      // Bedrock omits role on content_block_stop (it defaults to ROLE_SYSTEM), so only
      // check partial state — the same pattern as message_stop.
      // Re-read the last LLM turn: apply the same sentinel skip as lastLLMMessage (ignore
      // non-partial system turns, which are search-progress UI sentinels, not LLM protocol
      // turns) AND pick up any turn synthesised by a preceding content_block_delta (stale
      // lastLLMMessage). A search_update fired during tool-result streaming inserts a
      // system turn after the partial user turn; without this skip, turns.at(-1) returns
      // that system turn and the partial check false-fires (WEBAPP-K).
      let openTurnStopIndex = turns.length - 1;
      while (openTurnStopIndex >= 0) {
        const t = turns[openTurnStopIndex];
        if (!(t?.role === ROLE_SYSTEM && !t?.partial)) break;
        openTurnStopIndex--;
      }
      const openTurnStop = openTurnStopIndex >= 0 ? turns[openTurnStopIndex] : null;
      const {contentBlockIndex: index = eventData.index || 0} = eventData;
      if (!openTurnStop?.partial) {
        // No open partial turn — genuine protocol error (missing message_start).
        const error = this._handleStreamError(turns, openTurnStopIndex, event);
        if (error) turns.push(error);
      }
      else if (openTurnStop.content[index] && !openTurnStop.content[index].partial) {
        // A stop for a block that already closed is a duplicate (PLEX-6610: the backend's
        // error-path stop named block 0 whatever block was open). Keep the block as it is and
        // don't close it a second time: its tool input is already parsed, and its tool result's
        // sub-agent turns were already handled.
        this._captureStreamAnomaly(
          `content_block_stop[${index}] on missing/non-partial block — already closed, kept`,
          {
            level: "warning",
            // Its own fingerprint, so duplicate stops are counted apart from a missing block.
            fingerprint: ["content-block-stop-duplicate", "content_block_stop"],
            tags: { index, role: openTurnStop.role, agentId, mainAgentId: this.agentId,
                    streamId: this.streamId },
          }
        );
      }
      else {
        if (!openTurnStop.content[index]) {
          // Turn is open but content block at this index is missing.
          // Possible causes: content_block_start or content_block_delta was dropped
          // (SSE replay gap, network loss, or 100-breadcrumb cap hid the event from
          // diagnostics). Synthesize an empty stub so the stop can close cleanly.
          this._captureStreamAnomaly(
            `content_block_stop[${index}] on missing/non-partial block — synthesising stub`,
            {
              level: "warning",
              fingerprint: ["missing-content-block", "content_block_stop"],
              tags: { index, role: openTurnStop.role, agentId, mainAgentId: this.agentId,
                      streamId: this.streamId },
            }
          );
          openTurnStop.content[index] = { partial: true };
        }
        const contentBlock = openTurnStop.content[index];
        delete contentBlock.partial;
        if (contentBlock.toolResult) {
          const {toolUseId} = contentBlock.toolResult;
          // The sub-agent has reached end_turn — no more UI updates are coming for
          // it, so there's no reason to keep its turns MobX-observable. Replace the
          // live observable.array with a plain optimized array; the map is
          // observable.shallow, so swapping the value doesn't re-wrap it.
          const liveMessages = this.subAgentTurns.get(toolUseId);
          if (liveMessages) {
            const _toJsStart = Date.now();
            // Strip redundant toolResult content immediately — it's already retained
            // in full via this turn's own toolResult content block, so keeping it a
            // second time here just duplicates the largest payloads (fetch_results,
            // fetch_direct_relationships, etc.) for the rest of the session's lifetime.
            const optimized = optimizeAgentMessages(toJS(liveMessages));
            if (optimized) {
              this.subAgentTurns.set(toolUseId, optimized);
            } else {
              this.subAgentTurns.delete(toolUseId);
            }
            const _toJsMs = Date.now() - _toJsStart;
            if (_toJsMs > 50) {
              Analytics.event('chat-tojs-perf', {
                duration_ms: _toJsMs,
                message_count: liveMessages.length,
                tool_use_id: toolUseId,
              });
            }
          }
        } else if (contentBlock.toolUse) {
          const serialized = contentBlock.toolUse.input;
          contentBlock.toolUse.input = safeParseJSON(serialized) || {error: serialized};
        }
      }
    }
    else if (mtype === "message_stop") {
      console.debug(`[LLMClient] message_stop agentId=${agentId?.slice(-6)} stopReason=${stopReason} lastMsg.partial=${lastLLMMessage?.partial} lastMsg.role=${lastLLMMessage?.role}`);
      if (lastLLMMessage?.partial) {
        // Close the open partial turn (assistant or user/tool-result).
        lastLLMMessage.stopReason = stopReason;
        delete lastLLMMessage.partial;
        //{"type": "message_stop","amazon-bedrock-invocationMetrics": {"inputTokenCount": 11414,"outputTokenCount": 62,"invocationLatency": 5665,"firstByteLatency": 3598}}
        cleanTurn(lastLLMMessage);
        if (lastLLMMessage.role === ROLE_ASSISTANT) {
          Analytics.event(Analytics.EVENTS.AGENT_TURN_COMPLETE, {
            agent_id: agentId,
            is_root: !agentId || agentId === this.agentId,
            stop_reason: stopReason,
            stream_id: this.streamId,
            via_polling: this.pollingMode ?? false,
            duration_ms: lastLLMMessage.ts ? Date.now() - lastLLMMessage.ts : undefined,
          });
        }
        // Notify on every completed turn (root or sub-agent) so callers can persist
        // incremental progress. Sub-agent message_stop fires here too, giving a save
        // checkpoint after each tool-use stop and each sub-agent turn completion.
        if (this._onAssistantTurnComplete) {
          this._onAssistantTurnComplete();
        }
      } else if (!lastLLMMessage || lastLLMMessage.role !== ROLE_ASSISTANT) {
        // message_stop with no open partial turn and no completed assistant turn — Bedrock sent a
        // terminal event without a preceding message_start (e.g. content_filtered fires before
        // generation begins). Synthesize a closed assistant turn and capture to Sentry.
        if (stopReason) {
          // Stops that don't warrant a Sentry anomaly report. content_filtered is expected
          // (Bedrock refused before generation began) — backend emits the Sentry issue and it
          // routes to #monitoring; no need for a second capture here.
          const SENTRY_SILENT_STOPS = ["end_turn", "tool_use", "max_tokens", "stop_sequence", "content_filtered"];
          const isAbnormalStop = !SENTRY_SILENT_STOPS.includes(stopReason);
          turns.push({
            role: ROLE_ASSISTANT,
            content: [{text: `(${stopReason})`}],
            stopReason,
            status: (isAbnormalStop || stopReason === "content_filtered") ? "error" : undefined,
            ts: Date.now(),
          });
          if (stopReason === "content_filtered") {
            const lastUserTurn = [...turns].reverse().find(t => t.role === ROLE_USER);
            const promptText = lastUserTurn?.content?.find(b => b.text)?.text ?? "";
            const { cell } = getConfig();
            Analytics.event(Analytics.EVENTS.CHAT_CONTENT_FILTERED, {
              agent,
              agentId,
              cell,
              user: this._userStore?.userName,
              tenant: this._userStore?.tenantID,
              prompt: promptText,
            });

            const fallbackModel = this._appStatusStore?.aiModelContentFilterFallback;
            const isRetry = !!(this._lastSendParams?.modelOverride);
            if (agentId === this.agentId && fallbackModel && !isRetry && lastUserTurn) {
              runInAction(() => {
                const filteredIdx = turns.findLastIndex(t => t.stopReason === "content_filtered");
                if (filteredIdx !== -1) turns.splice(filteredIdx, 1);
              });
              const userTurnIdx = turns.lastIndexOf(lastUserTurn);
              this.replayFrom(userTurnIdx, {...(this._lastSendParams ?? {}), modelOverride: fallbackModel});
            }
          } else if (isAbnormalStop) {
            this._captureStreamAnomaly(`Bedrock ${stopReason} on ${agent} (no message_start)`, {
              level: "warning",
              fingerprint: ["bedrock-abnormal-stop", stopReason, agent],
              tags: {
                stopReason,
                agent,
                agentId,
                mainAgentId: this.agentId,
                streamId: this.streamId,
              },
            });
          }
        }
      } else {
        // message_stop without an open partial message — nothing to close
        // (message_stop events often omit role, so we only check partial state)
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
    }
    else if (mtype === "metadata") {
      if (!lastLLMMessage) {
        // Only a synthetic search-status system turn exists — no LLM turn to attach metadata to.
      } else if (role !== lastLLMMessage?.role) {
        const error = this._handleStreamError(turns, lastLLMMessageIndex, event);
        if (error) turns.push(error);
      }
      else {
        const {usage = {}, metrics = {}} = eventData.metadata || {};
        const {
          inputTokens = 0,
          outputTokens = 0,
          totalTokens = 0,
          cacheReadInputTokens = 0,
          cacheWriteInputTokens = 0
        } = usage;
        const {latencyMs = 0} = metrics;
        this.inputTokens += +inputTokens;
        this.outputTokens += +outputTokens;
        this.totalTokens += +totalTokens;
        this.cacheReadInputTokens += +cacheReadInputTokens;
        this.cacheWriteInputTokens += +cacheWriteInputTokens;
        this.latencyMs += +latencyMs;
        lastLLMMessage.metadata = {usage, metrics};
      }
    }
    else if (mtype === "ping" || mtype === "heartbeat") {
    }
    else if (mtype === "toast") {
      toast.info(eventData?.text || "Model enabled", {autoClose: 5000});
    }
    else if (mtype === "interrupt") {
      const { status = "interrupt" } = eventData || {};
      this.handleInterrupt(lastMessage, status === "cancel");
    }
    else if (mtype === "message") {
      // complete toolResult message is also sent when fully assembled
      if (eventData.role === ROLE_ASSISTANT && agentId === this.agentId && stopReason !== "tool_use") {
        // Conversation has completed normally, set state appropriately
        this.active = false;
        this.streamId = null;
      }
      // When message_stop(content_filtered) fires before generation begins, the client
      // synthesizes a placeholder turn. The server follows up with a message event carrying
      // the full content (remediation text). Patch it in here so the user sees guidance.
      if (stopReason === "content_filtered" && eventData.role === ROLE_ASSISTANT) {
        const serverContent = eventData.content;
        if (serverContent?.length) {
          const lastAssistant = [...turns].reverse().find(t => t.role === ROLE_ASSISTANT && t.stopReason === "content_filtered");
          if (lastAssistant) {
            const remediationBlocks = serverContent.filter(b => b.text && !b.text.match(/^\(content_filtered\)$/));
            if (remediationBlocks.length) {
              lastAssistant.content = remediationBlocks;
            }
          }
        }
      }
      // Apply server-driven compaction state if the server compacted this turn.
      // summary/offset tell the client which original turns were replaced so it can
      // preserve full turn history for UI display while using the compact form for LLM calls.
      if (agentId === this.agentId && eventData.metadata?.compaction) {
        const {summary, offset} = eventData.metadata.compaction;
        runInAction(() => {
          this._compaction = {summary, offset, compactedAt: Date.now()};
        });
      }
      // A fully-assembled turn (user tool-result or assistant) is a save checkpoint
      if (this._onAssistantTurnComplete) {
        this._onAssistantTurnComplete();
      }
    }
    else if (mtype === "error" && role === ROLE_ASSISTANT && stopReason === "error") {
      // Assistant-reported error (e.g., validation error from server/LLM)
      // Preserve as ASSISTANT error, don't convert to SYSTEM error
      if (lastMessage?.role === ROLE_ASSISTANT && (lastMessage?.partial || lastMessage?.stopReason === "error")) {
        // Add error content to the partial ASSISTANT turn (from message_start), or to a turn
        // already closed by a preceding message_stop(stopReason=error) in the same synthetic sequence
        const errorContent = eventData?.content || [{text: "Unknown error"}];
        errorContent.forEach(block => {
          lastMessage.content.push(block);
        });
        // Complete the partial ASSISTANT turn with error status
        lastMessage.stopReason = stopReason;
        lastMessage.status = "error";
        delete lastMessage.partial;
        cleanTurn(lastMessage);
        console.debug(`Completed ASSISTANT turn with error status at index ${lastMessageIndex}`, lastMessage);
      } else {
        // No partial ASSISTANT turn exists - this can happen with validation errors
        // that occur before message_start is sent. Create a new ASSISTANT error turn.
        const errorContent = eventData?.content || [{text: "Unknown error"}];
        const assistantError = {
          role: ROLE_ASSISTANT,
          content: errorContent,
          stopReason: stopReason,
          status: "error",
          ts: Date.now()
        };
        if (agentId) {
          assistantError.agentId = agentId;
        }
        if (eventData.agent) {
          assistantError.agent = eventData.agent;
        }
        turns.push(assistantError);
        console.debug(`Created new ASSISTANT error turn at index ${turns.length - 1}`, assistantError);
      }
      // Capture to Sentry so server-side errors surface in #issues
      const serverMsg = eventData?.content?.map(b => b.text || "").join("") || "Unknown server error";
      const conversationCapture = this._buildConversationCapture({});
      captureMessage(`Server error: ${serverMsg.substring(0, 150)}`, {
        level: "error",
        fingerprint: ["server-reported-error", agent],
        tags: {
          agent,
          agentId,
          mainAgentId: this.agentId,
          streamId: this.streamId,
          sessionAgent: this.sessionAgent,
          errorCategory: "server-error",
          conversationLength: turns.length,
        },
        ...conversationCapture,
      });
      // Close stream and mark as inactive
      // console.debug, not .warn (see PLEX-6334 / WEBAPP-DJ): embeds
      // agentId/mainAgentId per call, so Sentry's captureConsoleIntegration
      // can't group occurrences and spawns a fresh noise issue each time
      // (this is WEBAPP-AA).
      console.debug(
        `[LLMClient] mtype=error closing root stream: agentId=${agentId} mainAgentId=${this.agentId} ` +
        `agentIdMatchesRoot=${agentId === this.agentId} serverMsg=${JSON.stringify(serverMsg.slice(0, 150))}`
      );
      this.active = false;
      this.streamId = null;
    }
    else {
      let messageText;
      // Resolved connection state: prefer SSE EventSource connectionState; fall back to
      // useStreamConnector's reason field (set on fetch-based streaming error objects).
      const connState = event.connectionState
        || (event.reason === "network_error" ? "OPEN" : null)
        || (event.reason === "bad_response" ? "CONNECTING" : null)
        || (event.reason === "poll_error" ? "POLLING" : null);
      if (mtype !== "error") {
        messageText = "Unexpected event";
      } else {
        // Extract error information from the event
        const errorMessage = eventData?.error
                             || eventData?.content?.map(b => b.text || "").join("")
                             || null;

        // Distinguish between actual connection errors and server-reported errors.
        if (connState && !errorMessage) {
          // True connection error (no error message from server)
          messageText = `Connection error (state: ${connState})`;
        } else if (connState && errorMessage) {
          // Server error delivered via SSE
          messageText = `Server error (state: ${connState}): ${errorMessage}`;
        } else if (errorMessage) {
          // Server error without connection state
          messageText = errorMessage;
        } else {
          // No error message at all
          messageText = event.reason ? `Connection lost (${event.reason})` : "Connection lost";
        }
      }
      const streamInfo = DEV ? ` (${this.streamId})` : "";

      // Propagate connState into the event so _handleStreamError classifies it correctly.
      const effectiveEvent = (connState && !event.connectionState)
        ? { ...event, connectionState: connState }
        : event;
      const error = this._handleStreamError(turns, lastMessageIndex, effectiveEvent,
                                            `${messageText}${streamInfo}`);
      if (error) turns.push(error);
    }
  }

  handleInterrupt(lastMessage = null, cancel = false) {

    // Dedup: the server raises UserInterrupt independently in the root agent's
    // loop AND in every still-running sub-agent's loop for a single user cancel
    // action (each has its own check_for_interrupt() call site in interactive.py),
    // and each publishes its own "interrupt" SSE event on the same stream_id
    // channel. Without this guard, every one of those echoes re-runs the append
    // logic below and produces a repeating "I was interrupted" bubble for what
    // was a single user action. Only the first interrupt for the current send
    // cycle does the turn-mutation work; later ones just make sure
    // busy/connecting state is (still) cleared and stop.
    const alreadyHandled = this._interruptHandled;
    this._interruptHandled = true;
    // Invalidate any in-flight send (_sendPrompt/_continueFromToolResults) for the
    // cycle being interrupted, so its .then()/.catch() recognizes itself as stale
    // and skips reviving streamId/waitingForResponse once it resolves.
    this._generation++;

    this.streamId = null;
    this.waitingForResponse = false;

    if (alreadyHandled) {
      return;
    }

    if (cancel) {
      // Mark the last partial turn as cancelled so the UI can distinguish it from truncation.
      // Append *User Interrupt* and the follow-up prompt — especially important when the user
      // cancelled due to a long delay and was never shown the interrupted-response prompt.
      const cancelTurns = this.turns;
      const lastPartial = cancelTurns.length ? cancelTurns.at(-1) : null;
      if (lastPartial?.partial) {
        lastPartial.partial = false;
        lastPartial.cancelled = true;
        const lastBlock = lastPartial.content?.at(-1);
        const interruptSuffix = "\n\n*User Interrupt*";
        if (lastBlock?.text !== undefined) {
          lastBlock.text = (lastBlock.text || "") + interruptSuffix;
        } else {
          lastPartial.content = [...(lastPartial.content || []), { text: interruptSuffix.trim() }];
        }
      }
      // Add synthetic assistant follow-up so the user knows they can redirect
      cancelTurns.push({
        role: ROLE_ASSISTANT,
        ts: Date.now(),
        content: [{ text: "I was interrupted. What would you like me to do next?" }],
        cancelled: true,
      });
      return;
    }

    const turns = this.turns;
    if (lastMessage === null) {
      lastMessage = turns.length ? turns.at(-1) : null;
    }

    // Helper function to ensure all tool uses have corresponding tool results
    const ensureToolResultsComplete = (userTurn) => {
      const lastAssistantTurn = turns.slice().reverse().find(turn => turn.role === ROLE_ASSISTANT);
      if (lastAssistantTurn) {
        const expectedToolUseIds = lastAssistantTurn.content
          .filter(b => b.toolUse)
          .map(b => b.toolUse.toolUseId);

        const existingToolResultIds = userTurn.content
          .filter(b => b.toolResult)
          .map(b => b.toolResult.toolUseId);

        const missingToolUseIds = expectedToolUseIds.filter(id => !existingToolResultIds.includes(id));

        // Add missing tool results as interrupted
        missingToolUseIds.forEach(toolUseId => {
          userTurn.content.push({
                                   toolResult: {
                                     toolUseId,
                                     content: [{ text: "User interrupt" }],
                                     status: "error"
                                   }
                                 });
        });
      }
    };

    // Clean up conversation state to prepare for new user input
    if (!lastMessage?.partial) {
      // Complete message exists
      if (lastMessage?.role === ROLE_USER) {
        // Ensure all tool results are present
        ensureToolResultsComplete(lastMessage);
        // Add assistant response
        turns.push({
                     role: ROLE_ASSISTANT,
                     content: [{ text: "I was interrupted. What would you like me to do next?" }],
                     stopReason: "user_interrupt"
                   });
      } else if (lastMessage?.role !== ROLE_ASSISTANT) {
        turns.push({
                     role: ROLE_ASSISTANT,
                     content: [{ text: "I was interrupted. What would you like me to do next?" }],
                     stopReason: "user_interrupt"
                   });
      } else {
        // Complete assistant turn. If it already finished naturally (end_turn/tool_use),
        // don't append an interrupt message — the LLM completed before the interrupt
        // was processed, producing the contradictory "complete + interrupted" display
        // (PLEX-5826). Only annotate if the turn was stopped for an unexpected reason.
        const alreadyComplete = lastMessage.stopReason === "end_turn"
          || lastMessage.stopReason === "tool_use"
          || lastMessage.stopReason === "max_tokens"
          || lastMessage.stopReason === "stop_sequence";
        if (!alreadyComplete) {
          lastMessage.content.push({ text: "I was interrupted. What would you like me to do next?" });
          lastMessage.stopReason = "user_interrupt";
        }
      }
    } else {
      // Partial message exists - need to clean it up
      delete lastMessage.partial;

      if (lastMessage?.role === ROLE_ASSISTANT) {
        // Interrupted during assistant response
        // Mark any partial content as interrupted and update message
        lastMessage.content.forEach(contentBlock => {
          if (contentBlock.partial) {
            delete contentBlock.partial;
            if (contentBlock.text) {
              contentBlock.text += " [interrupted]";
            }
          }
        });

        // Append interruption message only if no toolUse blocks are present.
        // A turn with toolUse blocks that receives a trailing text block produces
        // [text, toolUse, text] — Connexion rejects this (toolUse.input must be last).
        if (!lastMessage.content.some(b => b.toolUse)) {
          lastMessage.content.push({ text: "I was interrupted. What would you like me to do next?" });
        }
        lastMessage.stopReason = "user_interrupt";
      } else if (lastMessage?.role === ROLE_USER) {
        // Interrupted during tool results - complete any partial tool results
        lastMessage.content.forEach(contentBlock => {
          if (contentBlock.partial) {
            delete contentBlock.partial;
            if (contentBlock.toolResult) {
              // Complete tool result with interrupt message
              contentBlock.toolResult.content = [{ text: "User interrupt" }];
              contentBlock.toolResult.status = "error";
            }
          }
        });
        // Find any expected tool results that are missing and add them as interrupted
        const lastAssistantTurn = turns.slice().reverse().find(turn => turn.role === ROLE_ASSISTANT);
        if (lastAssistantTurn) {
          const expectedToolUseIds = lastAssistantTurn.content
            .filter(b => b.toolUse)
            .map(b => b.toolUse.toolUseId);

          const existingToolResultIds = lastMessage.content
            .filter(b => b.toolResult)
            .map(b => b.toolResult.toolUseId);

          const missingToolUseIds = expectedToolUseIds.filter(id => !existingToolResultIds.includes(id));

          // Add missing tool results as interrupted
          missingToolUseIds.forEach(toolUseId => {
            lastMessage.content.push({
                                       toolResult: {
                                         toolUseId,
                                         content: [{ text: "User interrupt" }],
                                         status: "error"
                                       }
                                     });
          });
        }

        // Add assistant response asking for more input (this ensures USER -> ASSISTANT alternation)
        turns.push({
                     role: ROLE_ASSISTANT,
                     content: [{ text: "I was interrupted. What would you like me to do next?" }],
                     stopReason: "user_interrupt"
                   });
      }
    }

    // Defensive cleanup: remove any null/undefined turns that may have been introduced
    // by race conditions or edge cases in the interrupt handling path.
    const nullCount = turns.filter(t => t == null).length;
    if (nullCount > 0) {
      this._captureStreamAnomaly(
        `cancelStream: ${nullCount} null/undefined turn(s) found in conversation`,
        {
          level: "error",
          fingerprint: ["null-turns-in-conversation"],
          tags: { streamId: this.streamId, agentId: this.agentId },
          extra: {
            nullCount,
            totalTurns: turns.length,
            ...this._buildConversationCapture().context,
          },
        }
      );
      turns.splice(0, turns.length, ...turns.filter(t => t != null));
    }
  }

  _updateMetadata = (turn) => {
    if (!turn) return;
    // Sub-agent turns (subAgentTurns) never contribute their own usage/metrics —
    // they're merged into the parent tool-use turn's totals server-side. No
    // recursion needed; there is no nested structure to walk.
    const metadata = turn.metadata || turn;
    const {usage = {}, metrics = {}} = metadata;
    const {
      inputTokens = 0,
      outputTokens = 0,
      totalTokens = 0,
      cacheReadInputTokens = 0,
      cacheWriteInputTokens = 0
    } = usage;
    const { latencyMs = 0 } = metrics;
    this.inputTokens += +inputTokens;
    this.outputTokens += +outputTokens;
    this.totalTokens += +totalTokens;
    this.cacheReadInputTokens += +cacheReadInputTokens;
    this.cacheWriteInputTokens += +cacheWriteInputTokens;
    this.latencyMs += +latencyMs;
  };

  _calculateMetrics = () => {
    this.inputTokens = this.outputTokens = this.totalTokens = this.cacheReadInputTokens = this.cacheWriteInputTokens = this.latencyMs = 0;
    this.turns.forEach((turn) => {
      this._updateMetadata(toJS(turn));
    });
    // Each sub-agent (run_analyst, etc.) invokes its own LLM call and accrues its own
    // usage/metrics server-side — those turns carry their own metadata and must be
    // folded in separately; they're not included in the root turns' totals.
    this.subAgentTurns.forEach((messages) => {
      toJS(messages).forEach((turn) => this._updateMetadata(turn));
    });
  }

  loadConversation = ({turns: parsed, compaction = null, subAgentTurns = null}) => {
    try {
      // Detect and log null/undefined turns in stored conversation data
      const nullIndices = parsed.reduce((acc, t, i) => (t == null ? [...acc, i] : acc), []);
      if (nullIndices.length > 0) {
        captureMessage("loadConversation: null/undefined turns in stored conversation", {
          level: "warning",
          fingerprint: ["load-conversation-null-turns"],
          tags: {nullCount: nullIndices.length},
          data: {nullIndices, parsedLength: parsed.length, parsedTurns: parsed},
        });
      }
      // Flat accumulator for sub-agent turns, keyed by toolUseId, merged into
      // this.subAgentTurns at the end. Fed from three sources: the new top-level
      // subAgentTurns param, back-compat per-turn turn.agentMessagesCache (older
      // persisted format), and the "search-analyst" role back-compat path below.
      const loadedSubAgentTurns = {...(subAgentTurns || {})};
      const convo = [];
      let currentAgentId = this.agentId;
      parsed.filter(turn => turn != null).forEach(turn => {
        if (!turn.agent) {
          turn.agent = this.sessionAgent;
        }
        if (turn.role === undefined || turn.role === null) {
          captureMessage("loadConversation: turn with missing role", {
            level: "error",
            fingerprint: ["load-conversation-missing-role"],
            tags: {turnIndex: String(parsed.indexOf(turn))},
            data: {
              turn: JSON.stringify(turn),
              parsedLength: parsed.length,
              allRoles: parsed.map((t, i) => ({i, role: t?.role})),
            },
          });
          return; // skip — can't process a turn without a role
        }
        // Backwards-compatibility, temporary
        if (turn.role.startsWith("search-analyst")) {
          turn.agent = AGENT_ANALYST;
          turn.role = turn.role === "search-analyst" ? ROLE_ASSISTANT : ROLE_USER;
          if (!loadedSubAgentTurns[currentAgentId]) {
            loadedSubAgentTurns[currentAgentId] = [];
          }
          loadedSubAgentTurns[currentAgentId].push(cleanTurn(turn));
          return;
        }
        if (turn.role === "tool") {
          // All tool roles become user (tool results)
          turn.role = ROLE_USER;
          turn.agentId = this.agentId;
        }
        // Back-compat: fold an older-format per-turn agentMessagesCache into the
        // flat top-level accumulator, keyed the same way (by toolUseId).
        if (turn.agentMessagesCache) {
          Object.entries(turn.agentMessagesCache).forEach(([toolUseId, messages]) => {
            loadedSubAgentTurns[toolUseId] = messages;
          });
          delete turn.agentMessagesCache;
        }
        if (turn.role === ROLE_ASSISTANT) {
          if (turn.content.some(b => b.toolUse)) {
            currentAgentId = turn.content.reduce((result, b) => result || b.toolUse?.toolUseId, null);
          }
        }
        // End backward compatibility adjustment

        // Modify all top-level agent IDs to be consistent
        turn.agentId = this.agentId;
        cleanTurn(turn);
        convo.push(turn);
      });

      // Repair any assistant turns that have unpaired toolUse blocks (interrupted sessions,
      // or conversations where a prior error left a role=system turn instead of a toolResult).
      // For each such turn, strip the invalid junk following it and inject a proper
      // toolResult(error) + assistant(error-ack) pair so the conversation is structurally
      // valid and the user can continue without hitting a Bedrock ValidationException.
      //
      // We scan all assistant turns (not just the last) because a mid-conversation unpaired
      // toolUse (followed by more turns) is equally invalid from Bedrock's perspective.
      let i = 0;
      while (i < convo.length) {
        const turn = convo[i];
        if (turn.role !== ROLE_ASSISTANT) { i++; continue; }

        const toolUseIds = (turn.content || []).filter(b => b.toolUse).map(b => b.toolUse.toolUseId);
        if (toolUseIds.length === 0) { i++; continue; }

        // Find which IDs are covered by the immediately following user turn's toolResults.
        // Also clean up any incomplete state on that turn — partial flags and invalid toolResult
        // blocks (no content) are signs of a stream that was interrupted before cleanup ran.
        // A partial user turn mid-conversation should never happen; strip it defensively here.
        const nextTurn = convo[i + 1];
        if (nextTurn?.role === ROLE_USER) {
          // Remove toolResult blocks with no content — they are unusable and must not reach the API
          nextTurn.content = nextTurn.content.filter(block => {
            if (block.toolResult && !block.toolResult.content?.length) {
              console.debug(`loadConversation: dropping incomplete toolResult for ${block.toolResult.toolUseId}`);
              return false;
            }
            return true;
          });
          // Strip any lingering partial flags from blocks and the turn itself
          nextTurn.content.forEach(block => { delete block.partial; });
          delete nextTurn.partial;
        }
        const coveredIds = new Set(
          (nextTurn?.role === ROLE_USER
            ? (nextTurn.content || []).filter(b => b.toolResult).map(b => b.toolResult.toolUseId)
            : [])
        );
        const missing = toolUseIds.filter(id => !coveredIds.has(id));
        if (missing.length === 0) { i++; continue; }

        // Insert placeholder toolResult(s) immediately after this assistant turn, before
        // whatever follows (system errors, more turns, etc.). This preserves any valid turns
        // that came later in the conversation rather than truncating them.
        console.debug(`loadConversation: repairing unpaired toolUse at index ${i} (missing results for ${missing})`);
        const placeholderTurn = {
          role: ROLE_USER,
          content: missing.map(id => ({
            toolResult: {
              toolUseId: id,
              content: [{text: "Tool call failed: session was interrupted"}],
              status: "error"
            }
          })),
          stopReason: "error",
          status: "error",
          ts: Date.now()
        };
        // Strip any role=system turns immediately following before inserting placeholder,
        // since they are now represented by the toolResult error content.
        let insertAt = i + 1;
        while (insertAt < convo.length && convo[insertAt].role === ROLE_SYSTEM) {
          insertAt++;
        }
        // If the turn immediately after the system errors is also a user turn,
        // merge the placeholder toolResult content into it rather than inserting
        // a separate turn — Bedrock requires strictly alternating user/assistant.
        const turnAfterGap = insertAt < convo.length ? convo[insertAt] : null;
        if (turnAfterGap?.role === ROLE_USER) {
          turnAfterGap.content = [...placeholderTurn.content, ...turnAfterGap.content];
          convo.splice(i + 1, insertAt - (i + 1)); // just remove the system turns
        } else {
          convo.splice(i + 1, insertAt - (i + 1), placeholderTurn);
        }
        i += 2; // skip past the assistant turn and the new/merged user turn
      }

      // Detect a stale-partial last turn — turn completed on the backend but partial flag
      // was not cleared before persistence (e.g. tab backgrounded during final SSE flush,
      // render deferred, or cleanTurn not called on the saved snapshot).
      // Repair: strip partial, append truncation notice so the user knows the response may
      // be incomplete, and fire a backend snapshot + Sentry capture for forensic analysis.
      const lastConvoTurn = convo.length ? convo.at(-1) : null;
      if (lastConvoTurn?.partial ||
          lastConvoTurn?.content?.some(b => b.partial)) {
        // Fire-and-forget snapshot against the stream that owned this conversation.
        // streamId is not available here (we are loading a stored convo, not a live stream),
        // so use the agentId path if the snapshot endpoint supports it.
        const agentIdForSnapshot = this.agentId;
        if (agentIdForSnapshot) {
          fetch(`/api/agent/${agentIdForSnapshot}/snapshot`)
            .then(r => r.ok ? r.json() : null)
            .then(snapshot => {
              captureMessage("loadConversation: stale partial turn repaired", {
                level: "warning",
                fingerprint: ["load-conversation-stale-partial"],
                tags: {
                  agentId: agentIdForSnapshot,
                  turnIndex: String(convo.length - 1),
                  turnRole: lastConvoTurn.role,
                },
                extra: {
                  partialBlocks: lastConvoTurn.content?.map((b, i) => ({
                    index: i,
                    partial: !!b.partial,
                    type: b.text ? "text" : b.toolUse ? "toolUse" : b.toolResult ? "toolResult" : "unknown",
                    preview: (b.text || JSON.stringify(b) || "").substring(0, 100),
                  })),
                  agentSnapshot: snapshot,
                },
              });
            })
            .catch(() => {
              captureMessage("loadConversation: stale partial turn repaired (no snapshot)", {
                level: "warning",
                fingerprint: ["load-conversation-stale-partial"],
                tags: { agentId: agentIdForSnapshot, turnIndex: String(convo.length - 1) },
              });
            });
        }
        // Repair the turn so the conversation is usable
        delete lastConvoTurn.partial;
        lastConvoTurn.content?.forEach(b => { delete b.partial; });
        const lastTextBlock = lastConvoTurn.content
          ? [...lastConvoTurn.content].reverse().find(b => b.text != null)
          : null;
        if (lastTextBlock) {
          lastTextBlock.text += "\n\n*(Response may be incomplete — session was interrupted)*";
        } else if (lastConvoTurn.content) {
          lastConvoTurn.content.push({ text: "*(Response may be incomplete — session was interrupted)*" });
        }
        console.debug("loadConversation: repaired stale partial turn at index", convo.length - 1);
      }

      //console.log("loadConversation: parsed", parsed.length, "turns, returning", convo.length, "turns");
      runInAction(() => {
        this.streamId = null;
        this._compaction = compaction || null;
        this._turns.length = 0;
        this._turns.push(...convo);
        this.subAgentTurns.clear();
        Object.entries(loadedSubAgentTurns).forEach(([toolUseId, messages]) => {
          if (messages?.length) this.subAgentTurns.set(toolUseId, messages);
        });
        this._calculateMetrics();
      });
      return this.turns;
    }
    catch(error) {
      console.error("Couldn't parse the conversation", error);
      console.error("Error details:", error.message, error.stack);
      captureMessage("Error during initialization from JSON",
                     "error", {parsedText: parsed, error: error.message, stack: error.stack});
    }
    return false;
  }

  _formatDocumentContentBlock = fileInfo => {
    // The attachment will actually be injected server-side from a file upload cache
    const format = MIME2FORMAT[fileInfo.type];
    if (!format) {
      const typeDesc = fileInfo.type === "" ? "empty string" :
                       fileInfo.type === null ? "null" :
                       fileInfo.type === undefined ? "undefined" :
                       `"${fileInfo.type}"`;
      // Browsers report an empty/unrecognized MIME type for extensions they don't
      // register (e.g. .plex conversation exports), even though FILE_INPUT_ACCEPT
      // declares a type for them — that's expected, not actionable, so debug not warn.
      console.debug(`File "${fileInfo.name}" has unsupported MIME type ${typeDesc}, using fallback format`);
    }
    // Keep track of the File object's metadata and upload identifier
    const fileBlock = {
      name: fileInfo.name,
      type: fileInfo.type,
      size: fileInfo.size,
      lastModified: fileInfo.lastModified
    };
    // Include upload ID if available (required for backend to retrieve uploaded file)
    if (fileInfo.uploadId) {
      fileBlock.uploadId = fileInfo.uploadId;
    }
    if (isImage(fileInfo)) {
      return {image: {format: format || "png", source: {file: fileBlock}}};
    }
    return {document: {format: format || "txt", name: fileInfo.name, source: {file: fileBlock}}};
  };

  _turnsForLLM = () => {
    if (this._compaction) {
      return [...this._compaction.summary, ...this._turns.slice(this._compaction.offset)];
    }
    return this._turns;
  };

  _canonicalTurns = turns => {
    // Track all document names seen across the entire conversation
    // to prevent Bedrock ValidationException: "Messages can't contain duplicate document names"
    const globalDocNames = new Set();
    const nameCounters = new Map(); // Track how many times each base name has been used

    return turns
      .filter(turn => turn != null)
      // Exclude content_filtered turns from API history — they're incomplete responses
      // that poison follow-up context (Bedrock would receive a partial assistant turn).
      .filter(turn => turn.stopReason !== "content_filtered")
      .map(({role, content}) => {
        const processedContent = content.flatMap(block => {
          // reasoningContent blocks are assistant-only thinking blocks returned by the API;
          // they must never be sent back in subsequent requests (Bedrock rejects them).
          if (block.reasoningContent !== undefined) return [];

          // Strip UI-only rendering hints before sending to the API
          if (block.rawMarkdown !== undefined || block.partial !== undefined || block.admin_detail !== undefined) {
            const {rawMarkdown: _, partial: __, admin_detail: ___, ...apiBlock} = block;
            block = apiBlock;
          }

          // toolUse.input must be an object (api.yaml: type: object). If the stream was
          // interrupted before content_block_stop, input is still a raw JSON string —
          // parse it here so Connexion doesn't reject with a 400.
          if (block.toolUse && typeof block.toolUse.input === 'string') {
            block = {...block, toolUse: {...block.toolUse, input: safeParseJSON(block.toolUse.input) || {}}};
          }

          // Check if this is a document or image block with a name
          const attachmentBlock = block.document || block.image;
          if (!attachmentBlock?.name) {
            return block;
          }

          const originalName = attachmentBlock.name;

          // If this exact name hasn't been seen, use it as-is
          if (!globalDocNames.has(originalName)) {
            globalDocNames.add(originalName);
            nameCounters.set(originalName, 1);
            return block;
          }

          // Name collision - generate a unique name by appending a counter
          const counter = nameCounters.get(originalName) || 1;
          const nameParts = originalName.match(/^(.+?)(\.[^.]+)?$/);
          const baseName = nameParts[1];
          const extension = nameParts[2] || '';

          let uniqueName = originalName;
          let attempt = counter;
          while (globalDocNames.has(uniqueName)) {
            uniqueName = `${baseName} (${attempt})${extension}`;
            attempt++;
          }

          globalDocNames.add(uniqueName);
          nameCounters.set(originalName, attempt);

          // Create a new block with the renamed document/image
          const renamedBlock = {...block};
          if (block.document) {
            renamedBlock.document = {...block.document, name: uniqueName};
          } else if (block.image) {
            renamedBlock.image = {...block.image, name: uniqueName};
          }

          return renamedBlock;
        })
        // Drop content blocks left fully empty by a synthesized content_block_stop
        // stub (missing/dropped content_block_start or delta) — Connexion 400s on
        // {} since it matches none of the union schemas (PLEX-3822 / WEBAPP-M).
        .filter(block => block.text !== undefined || block.toolUse || block.toolResult ||
          block.document || block.image);

        return {role, content: processedContent};
      })
      .filter(({role}) => role === ROLE_USER || role === ROLE_ASSISTANT);
  }

  _cancelAgents = (includeSelf = false) => {
    const agentIds = new Set(toJS(this.subAgentTurns.keys()));
    if (includeSelf) {
      agentIds.add(this.agentId);
      Analytics.event(Analytics.EVENTS.CHAT_CANCEL, {
        agent: this.sessionAgent,
        stream_id: this._streamId,
        elapsed_ms: this._streamStartTime ? Date.now() - this._streamStartTime : null,
        turn_count: this.turns.length,
      });
    }
    if (agentIds.size) {
      Promise.all(Array.from(agentIds)
                    .filter(agentId => agentId)
                    .map(agentId =>
                           cancelAgent(agentId, this.auth)))
        .catch(error => {
          console.warn("Error during agent cancel", error);
        })
        .finally((results) => {
          runInAction(() => {
            Array.from(agentIds).forEach(agentId => {
              if (this.subAgentTurns.has(agentId)) {
                this.subAgentTurns.delete(agentId);
              }
            });
          });
        });
    }
  };


  _extractAgentIds = turns => {
    return turns.reduce((result, m) => {
      result.push(...m.content.reduce((ids, b) => {
        const agentId = (b.toolUse || b.toolResult)?.toolUseId;
        if (agentId) {
          ids.push(agentId);
        }
        return ids;
      }, []));
      return result;
    }, [])
  };

  sendPrompt = ({
                  text,
                  attachments = null,
                  searchParams = null,
                  historyId = null,
                  ...params
                }) => {
    const prompt = {
      role: ROLE_USER,
      content: [{text: text}],
    };
    if (attachments && this.sessionAgent !== AGENT_HELP) {
      attachments.forEach(fileInfo => {
        prompt.content.push(this._formatDocumentContentBlock(fileInfo));
      });
    }
    return this._sendPrompt(prompt, {...params, searchParams, historyId});
  }

  _sendPrompt = (prompt, {
    chatSettings = {},
    templates = {},
    useMarkdown = true,
    searchParams = null,
    useTools = true,
    historyId = null,
    modelOverride = null,
  } = {}) => {
    this._lastSendParams = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId, modelOverride};
    this.active = true;
    this._staleEventReported = false;
    // Start a new send cycle. If the user interrupts before this cycle's fetch
    // resolves, handleInterrupt bumps past this value (see below), so the late
    // .then() can tell it's stale and must not revive streamId/busy state.
    const generation = ++this._generation;
    this._interruptHandled = false;

    const nullCount = this.turns.filter(t => t == null).length;
    if (nullCount > 0) {
      this._captureStreamAnomaly(
        `_sendPrompt: ${nullCount} null/undefined turn(s) in conversation at prompt start`,
        {
          level: "error",
          fingerprint: ["null-turns-at-send"],
          tags: { streamId: this.streamId, agentId: this.agentId },
          extra: {
            nullCount,
            totalTurns: this.turns.length,
            ...this._buildConversationCapture().context,
          },
        }
      );
      runInAction(() => {
        this.turns.splice(0, this.turns.length, ...this.turns.filter(t => t != null));
      });
    }

    // Check if the last turn is a user message
    // If so, append the new prompt content to it rather than creating a new turn
    // This prevents consecutive user messages which violates the alternation requirement
    const lastTurn = this.turns.length > 0 ? this.turns.at(-1) : null;

    // Only append to existing turn if both are user turns
    // Never append assistant content to a user turn or vice versa
    if (lastTurn?.role === ROLE_USER && prompt.role === ROLE_USER) {
      // Append the new prompt content to the existing user turn
      prompt.content.forEach(block => lastTurn.content.push(block));
    } else {
      // Create a new turn as usual
      this.turns.push({...prompt, ts: Date.now()});
    }

    this.waitingForResponse = true;
    const _sendStart = Date.now();
    console.debug(`[LLMClient] _sendPrompt: serializing turns`, {turnCount: this.turns.length});
    const _canonicalMessages = this._canonicalTurns(toJS(this._turnsForLLM()));
    const _serializeMs = Date.now() - _sendStart;
    console.debug(`[LLMClient] _sendPrompt: serialized ${_canonicalMessages.length} turns in ${_serializeMs}ms`);
    Analytics.event('chat-submit-perf', {
      serialize_ms: _serializeMs,
      turn_count: this.turns.length,
      canonical_count: _canonicalMessages.length,
      stream_id: this.streamId,
    });
    const postTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out waiting for server response")), POST_TIMEOUT()));
    return Promise.race([
      (this.sessionAgent === AGENT_HELP
            ? fetchHelp(toJS(prompt.content.map(el => el.text || "").join("")),
                        this.sessionId && `${this.sessionId}:${this.turns.length - 1}`,
                        this.auth)
            : fetchChatCompletion(searchParams || {},
                                  {
                                    model: modelOverride ?? this.model,
                                    ...chatSettings,
                                    agentId: this.agentId,
                                    stream: true,
                                    messages: _canonicalMessages,
                                    templates: templates,
                                    useMarkdown,
                                    useTools: useTools,
                                    agent: this.sessionAgent,
                                    historyId,
                                  }, this.auth)),
      postTimeout,
    ])
      .then(resp => {
        // Stale response guard: the user may have clicked interrupt while this
        // fetch was in flight. handleInterrupt() doesn't (and can't) cancel an
        // already-sent fetch — it only resets local state — so this .then() can
        // fire *after* the interrupt already put the UI back to idle. Reviving
        // streamId here would resurrect "Plexy is thinking...", "Still connecting
        // to Plexy...", and the stop button for a request the user already
        // cancelled. Only apply the response if no interrupt happened since this
        // cycle started.
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _sendPrompt: dropping stale response for generation ${generation} (current ${this._generation})`);
          return;
        }
        const {stream_id: streamId = null, session_id: sessionId} = resp;
        runInAction(() => {
          if (this.sessionAgent === AGENT_HELP) {
            this.sessionId = sessionId;
            // FIXME stream the response instead of sync call
            this.turns.push({role: ROLE_ASSISTANT, content: [{text: resp.response}]});
          }
          this.streamId = streamId;
        });
      })
      .catch(error => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _sendPrompt: dropping stale error for generation ${generation} (current ${this._generation})`);
          return;
        }
        this._handleError(error);
      })
      .finally(() => {
        if (generation === this._generation) {
          this.waitingForResponse = false;
        }
      });
  }

  // Truncate turns to newLength and release any subAgentTurns entry that was only
  // referenced by a discarded turn's toolUse block. subAgentTurns is flat and
  // keyed by toolUseId for the whole conversation — a toolUseId discarded here
  // stops being reachable from any remaining toolUse block, so its entry is dead
  // weight. Any that are still actively streaming (a live observable.array, not
  // yet swapped for the finalized plain array at toolResult close) get cancelled
  // rather than silently dropped, per the same rule _cancelAgents follows.
  _truncateTurns = (newLength) => {
    const discardedIds = new Set();
    for (let i = newLength; i < this.turns.length; i++) {
      (this.turns[i]?.content || [])
        .filter(b => b.toolUse)
        .forEach(b => discardedIds.add(b.toolUse.toolUseId));
    }
    for (let i = 0; i < newLength; i++) {
      (this.turns[i]?.content || [])
        .filter(b => b.toolUse)
        .forEach(b => discardedIds.delete(b.toolUse.toolUseId));
    }
    const stillActive = Array.from(discardedIds).filter(id => isObservableArray(this.subAgentTurns.get(id)));
    discardedIds.forEach(id => this.subAgentTurns.delete(id));
    this.turns.length = newLength;
    if (stillActive.length) {
      Promise.all(stillActive.map(agentId => cancelAgent(agentId, this.auth)))
        .catch(error => console.warn("Error cancelling discarded sub-agents", error));
    }
  }

  replayFrom = (index, params) => {
    this.streamId = null;
    const turn = this.turns[index];

    const isToolResultTurn = turn.role === ROLE_USER &&
                             turn.content?.every(b => b.toolResult ||
                                                      b.text?.match(/^\*\(Response was interrupted\)\*$/));

    if (isToolResultTurn) {
      // Keep all turns up through and including the tool results, then ask the LLM to continue.
      this._truncateTurns(index + 1);
      this._calculateMetrics();
      this._continueFromToolResults(params);
    } else {
      this._truncateTurns(index);
      this._calculateMetrics();
      if (turn.role === ROLE_USER || turn.stopReason === "tool_use") {
        this._sendPrompt(turn, params);
      }
    }
  }

  // Ask the LLM to continue from the current turns (which end with tool results).
  // Unlike _sendPrompt, this does not push a new turn — the tool results are already in turns.
  _continueFromToolResults = ({
    chatSettings = {},
    templates = {},
    useMarkdown = true,
    searchParams = null,
    useTools = true,
    historyId = null,
    modelOverride = null,
  } = {}) => {
    this.active = true;
    this._lastSendParams = {chatSettings, templates, useMarkdown, searchParams, useTools, historyId, modelOverride};
    this.waitingForResponse = true;
    // Start a new send cycle — see _sendPrompt for why this guards against a
    // late-resolving fetch reviving state after the user already interrupted.
    const generation = ++this._generation;
    this._interruptHandled = false;
    const postTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out waiting for server response")), POST_TIMEOUT()));
    Promise.race([
      fetchChatCompletion(searchParams || {},
                          {
                            model: modelOverride ?? this.model,
                            ...chatSettings,
                            agentId: this.agentId,
                            stream: true,
                            messages: this._canonicalTurns(toJS(this._turnsForLLM())),
                            templates,
                            useMarkdown,
                            useTools,
                            agent: this.sessionAgent,
                            historyId,
                          }, this.auth),
      postTimeout,
    ])
      .then(resp => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _continueFromToolResults: dropping stale response for generation ${generation} (current ${this._generation})`);
          return;
        }
        const {stream_id: streamId = null} = resp;
        runInAction(() => {
          this.streamId = streamId;
        });
      })
      .catch(error => {
        if (generation !== this._generation) {
          console.debug(`[LLMClient] _continueFromToolResults: dropping stale error for generation ${generation} (current ${this._generation})`);
          return;
        }
        this._handleError(error);
      })
      .finally(() => {
        if (generation === this._generation) {
          this.waitingForResponse = false;
        }
      });
  }

  // Keep the first `keepCount` turns, discard the rest.
  discardAfter = keepCount => {
    runInAction(() => {
      this.streamId = null;
      if (keepCount === 0) {
        this.sessionId = null;
      }
      this._truncateTurns(keepCount);
      this._calculateMetrics();
    });
  }

  interrupt = () => {
    this.handleInterrupt();
  }

  clear = () => {
    runInAction(() => {
      // Clear conversation state for a fresh start. Cancel any sub-agents still
      // actively streaming before dropping their entries (same rule as
      // _truncateTurns/_cancelAgents) rather than a bare clear().
      const stillActive = Array.from(this.subAgentTurns.entries())
        .filter(([, v]) => isObservableArray(v))
        .map(([id]) => id);
      this.streamId = null;
      this.turns.length = 0;
      this.subAgentTurns.clear();
      this.sessionId = null;
      this._calculateMetrics();
      if (stillActive.length) {
        Promise.all(stillActive.map(agentId => cancelAgent(agentId, this.auth)))
          .catch(error => console.warn("Error cancelling discarded sub-agents", error));
      }
    });
  }

  /**
   * Create conversation data with full context for export/replay
   * This includes metadata needed to properly restore, replay, and debug conversations
   */
  conversationWithContext = ({
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    addCurrentUser = false
  }) => {
    const { cell, env } = getConfig();
    const currentUser = this._userStore?.userName;

    // Determine authors list
    let authors = conversationContext?.authors ? [...conversationContext.authors] : [];

    if (addCurrentUser && currentUser && !authors.includes(currentUser)) {
      authors.push(currentUser);
    }

    if (authors.length === 0 && addCurrentUser && currentUser) {
      authors = [currentUser];
    }

    const context = {
      title: conversationTitle || this._generateFallbackTitle(),
      searchParams,
      editedTemplates,
      user: currentUser,
      userID: this._userStore?.userID,
      tenantID: this._userStore?.tenantID,
      authors: authors.length > 0 ? authors : undefined,
      cell: cell,
      env: env,
      timestamp: new Date().toISOString(),
      version: getVersionInfo(),
      model: this._model,
    };

    // Optimize sub-agent turns for persistence (strip redundant tool results,
    // preserve resolve/fetch_entities/validate_plex_ids in full) — same rule
    // cleanTurn used to apply per-turn, now applied once to the flat top-level map.
    const optimizedSubAgentTurns = {};
    this.subAgentTurns.forEach((messages, toolUseId) => {
      const optimized = optimizeAgentMessages(toJS(messages));
      if (optimized) optimizedSubAgentTurns[toolUseId] = optimized;
    });

    return {
      context,
      turns: this._turns.map(turn => cleanTurn(toJS(turn), true)),
      subAgentTurns: optimizedSubAgentTurns,
    };
  };

  _generateFallbackTitle = () => {
    if (this.turns.length === 0) return "Untitled Conversation";

    // Find first user message
    const firstUserTurn = this.turns.find(t => t != null && t.role === ROLE_USER);
    if (!firstUserTurn) return "Untitled Conversation";

    // Extract text from first user message
    const textBlocks = firstUserTurn.content?.filter(b => b.text) || [];
    if (textBlocks.length === 0) return "Untitled Conversation";

    const text = textBlocks[0].text.trim();
    // Use first 50 chars as title
    return text.length > 50 ? text.substring(0, 47) + "..." : text;
  };

  /**
   * Convert conversation to plain text representation
   */
  conversationAsText = ({includeToolUse = false, dsConfig = null, extractConversationText}) => {
    return this.turns
      .reduce((result, turn) =>
        `${result}\n${extractConversationText(turn.role, turn.content, includeToolUse, dsConfig)}`,
        ""
      );
  };

  /**
   * Download conversation as JSON or text
   */
  downloadConversation = async ({
    asJSON = false,
    asPlex = false,
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    dsConfig = null,
    extractConversationText = null,
    includeToolUse = false
  }) => {
    const conversationData = this.conversationWithContext({
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle
    });

    // Generate a proper title via LLM if we only have the fallback truncated version
    let title = conversationData.context.title;
    const firstUserTurn = this.turns.find(t => t != null && t.role === ROLE_USER);
    const firstText = firstUserTurn?.content?.find(b => b.text)?.text?.trim() || '';
    const isFallbackTitle = firstText && title === (firstText.length > 50 ? firstText.substring(0, 47) + "..." : firstText);
    if (isFallbackTitle && this._auth) {
      const conversationText = firstText.slice(0, 2000);
      const titlePrompt = `Generate a concise, descriptive title for this conversation.
Use natural language with proper capitalization. Maximum 60 characters.
Only return the title, nothing else.

Conversation:
${conversationText}`;
      title = await oneshot(titlePrompt, this._auth, { input_format: "text", output_format: "text" })
        .then(r => r.text())
        .then(t => t.trim().replace(/^["']|["']$/g, ''))
        .catch(() => title);
    }

    const filenameSlug = title
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
      : null;

    const baseFilename = filenameSlug || title || 'conversation';

    if (asJSON) {
      const text = JSON.stringify(conversationData);
      const blob = new Blob([text], { type: "application/json" });
      // Use .plex extension when asPlex flag is true
      const extension = asPlex ? '.plex' : '.json';
      saveBlob(blob, `${baseFilename}${extension}`);
    } else {
      if (!extractConversationText) {
        throw new Error("extractConversationText function required for text export");
      }
      const text = this.conversationAsText({includeToolUse, dsConfig, extractConversationText});
      const blob = new Blob([text], { type: "text/plain" });
      saveBlob(blob, `${baseFilename}.txt`);
    }
  };

  /**
   * Generate and download summary (DOCX or PPTX)
   */
  downloadSummary = async ({
    outputFormat = "pptx",
    verbose = false,
    searchParams = {},
    editedTemplates = {},
    conversationContext = null,
    conversationTitle = null,
    aiTemplates = {},
    onStart = () => {},
    onComplete = () => {},
    onError = () => {}
  }) => {
    onStart(outputFormat);

    const conversationData = this.conversationWithContext({
      searchParams,
      editedTemplates,
      conversationContext,
      conversationTitle
    });

    const title = conversationData.context.title;
    const filenameSlug = title
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
      : null;

    const options = {
      input_format: "markdown",
      output_format: outputFormat,
      mime_type: outputFormat === "pptx"
        ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      verbose,
      ...(filenameSlug && { filename: filenameSlug }),
    };

    // Prune conversation before sending: keep only text content from user/assistant turns.
    // Mirrors backend prune_conversation() — removes tool use, tool results, sub-agent
    // turns, and metadata that bloat the payload but aren't needed for report generation.
    // Use _turnsForLLM() so that compacted conversations include the summary turn instead of
    // the raw (pre-compaction) turns that conversationData.turns contains.
    const prunedData = {
      context: { title: conversationData.context.title },
      turns: this._turnsForLLM()
        .map(turn => {
          const textBlocks = (turn.content || []).filter(b => b.text).map(b => ({ text: b.text }));
          return textBlocks.length > 0 ? { role: turn.role, content: textBlocks } : null;
        })
        .filter(Boolean)
    };
    const conversationJSON = JSON.stringify(prunedData);

    const key = outputFormat === "pptx" ? "presentation-prompt" : "report-prompt";
    const baseTemplate = aiTemplates[key] || `Make a report about the following text\n${TEXT_PLACEHOLDER}`;
    const userCustomText = editedTemplates[key] || "";
    const prompt = userCustomText ? `${baseTemplate}\n\n${userCustomText}` : baseTemplate;
    const fullPrompt = prompt.replace(TEXT_PLACEHOLDER, `<input-text>${conversationJSON}</input-text>`);

    try {
      const response = await oneshot(fullPrompt, this._auth, options);

      const header = response.headers.get('content-disposition');
      let filename = `${filenameSlug || 'report'}.${outputFormat}`;

      if (header) {
        try {
          const disposition = parseContentDisposition(header);
          filename = disposition.parameters.filename || filename;
        } catch (e) {
          console.warn('Failed to parse Content-Disposition header:', header, e);
          captureMessage('Malformed Content-Disposition header in summary generation', {
            level: "warning",
            fingerprint: ["content-disposition-parse-error"],
            tags: {
              error_type: "content_disposition_parse",
              output_format: outputFormat,
            },
            context: {
              header: header,
              error: e.toString()
            }
          });
        }
      }

      const blob = await response.blob();
      saveBlob(blob, filename);
      onComplete(outputFormat);
    } catch (error) {
      console.error(`Failed to generate ${outputFormat}:`, error);
      const isTimeout = error.name === 'OneshotTimeoutError';
      captureMessage(`Summary generation ${isTimeout ? 'timed out' : 'failed'}: ${error.message}`, {
        level: "error",
        asException: true,
        errorName: isTimeout ? "SummaryGenerationTimeout" : "SummaryGenerationError",
        fingerprint: isTimeout ? ["summary-generation-timeout", outputFormat] : ["summary-generation-error", outputFormat],
        tags: {
          error_type: isTimeout ? "summary_timeout" : "summary_generation",
          output_format: outputFormat,
        },
        context: {
          summary_error: {
            error_message: error.message,
            output_format: outputFormat,
            verbose,
            conversation_turn_count: prunedData.turns.length,
            prompt_length: fullPrompt.length,
            conversation_json_length: conversationJSON.length,
          },
        },
        attachments: [
          {
            filename: "conversation.json",
            data: conversationJSON,
            contentType: "application/json",
          },
          {
            filename: "prompt.txt",
            data: fullPrompt,
            contentType: "text/plain",
          },
        ],
      });
      onError(outputFormat, error);
    }
  };

  dispose = () => {
    // Only stop streaming, don't clear conversation
    // The conversation should persist until explicitly discarded
    // or restored from storage on component remount
    this.streamId = null;
  };
}
